import { useEffect, useState, useRef, useMemo } from "react";
import socket from "../../socket";
import { apiFetch } from "../../utils/api";
import { logClientActivity } from "../../utils/clientLogger";

// useOrders is the logic center for managing order data.
// It handles real-time updates, organizes ticket items by course and category,
// and applies visibility rules based on settings.

export function useOrders({
  stationId,
  role,
  stationTimezone = "UTC",
  onVoidedItem = null,
  onCourseFired = null,
  onTicketFirePromotionRequested = null
}) {
  const mountIdRef = useRef(Math.random().toString(36).slice(2));
  const [orders, setOrders] = useState([]); // Holds the current list of orders
  const [settings, setSettings] = useState({
    max_items_per_ticket: Infinity,
    auto_fire_from_expo: true,
    firing_sync_method: "none",
    sync_firing_with_expo: false,
    sync_firing_with_all_stations: false,
    bumping_sync_method: "none",
    display_bump_status_of_other_units: false,
    display_bump_status_other_units_ticket: true,
    display_bump_status_other_units_course: false,
    display_bump_status_other_units_item: false,
    item_prep_display_mode: "normal",
    sync_courses_with_expo: false,
    parking_sync_method: "none",
    sync_parking_with_expo: false,
    sync_parking_with_all_stations: false,
    ticket_location_sync_method: "none",
    ticket_page_sync_method: "none",
    ticket_fire_promotion_mode: "none",
    enable_freeform_board: false,
    enable_line_item_board: false,
    default_order_destination: "normal",
    park_ticket_on_course_bump: false,
    unpark_ticket_when_items_are_fired: false,
    enable_bump_bar: false,
  }); // KDS settings, defaults the max_items_per_ticket setting to no limit
  const settingsRef = useRef(settings); // Keeps a live reference to the current settings object
  const [, forceUpdate] = useState(0); // Used to force re-renders every second
  const tapTimers = useRef({}); // Used for double-tap detection
  const categoryMapRef = useRef(new Map()); // Holds category coursing metadata
  const handledMsgByTicketRef = useRef(new Map());
  const addonSyncAckRef = useRef(new Set());
  const unparkTicketRef = useRef(() => {});
  const displayOnlyRefreshTimerRef = useRef(null);
  const remoteStateRefreshTimerRef = useRef(null);
  const bumpedTicketHistoryRef = useRef(new Set());
  const bumpedOrderHistoryRef = useRef(new Set());

  // --- helpers used by other helpers (must appear first) ---
  const normalizeId = id => String(id ?? "").trim();
  const normalizeTicket = id => String(id ?? "").split("-")[0].trim();
  const normalizeItem = id => String(id ?? "").split("-")[0].trim();
  const normalizeCategoryKey = value => String(value ?? "").trim().replace(/\s+/g, " ").toUpperCase();
  const isAddonFlag = v => v === true || v === 1 || v === "1";
  const logKdsActivity = (activity, context = {}) => logClientActivity(activity, {
    station_id: stationId,
    role,
    screen_id: mountIdRef.current,
    ...context,
  });
  // Keep this compact: client activity records have a size limit, but the index,
  // ID, and name are enough to compare POS, API, and display ordering.
  const summarizeItemOrder = (items = []) => (items || []).slice(0, 40).map((item, index) => ({
    index,
    id: normalizeId(item?.ID),
    name: String(item?.Name || "").slice(0, 80),
    category: String(item?.Category || "").slice(0, 60),
    course: Number.isFinite(Number(item?.course)) ? Number(item.course) : null,
    is_addon: !!item?.isAddon || isAddonFlag(item?.is_addon) || isAddonFlag(item?.isaddon),
    is_separator: !!item?.isCourseSeparator,
  }));
  const sameItemSequence = (before = [], after = []) =>
    before.length === after.length && before.every((item, index) =>
      String(item?.id ?? "") === String(after[index]?.id ?? ""));
  const isAlphabeticalItemOrder = (items = []) => {
    const names = items.map(item => String(item?.name || "").toLocaleUpperCase());
    return names.every((name, index) => index === 0 || names[index - 1] <= name);
  };
  const summarizeOrders = list => (list || []).map(order => ({
    ticket_instance_id: order?.ticket_instance_id ?? null,
    order_id: order?.order_id ?? null,
    active_items: (order?.items || []).filter(item => !item?.isBumped && !item?.isVoided).map(item => String(item?.ID ?? "")),
    bumped_items: (order?.items || []).filter(item => item?.isBumped).map(item => String(item?.ID ?? "")),
  }));
  const getAddonCourseDebugSnapshot = item => ({
    id: normalizeId(item?.ID),
    baseId: normalizeItem(item?.ID),
    name: String(item?.Name || "").slice(0, 80),
    category: String(item?.Category || "").slice(0, 80),
    course: Number.isFinite(Number(item?.course)) ? Number(item.course) : null,
    dbCourse: Number.isFinite(Number(item?._dbCourse)) ? Number(item._dbCourse) : null,
    isAddon: !!item?.isAddon || isAddonFlag(item?.is_addon) || isAddonFlag(item?.isAddon) || isAddonFlag(item?.isaddon),
    suppressAddonDisplay: !!item?.suppressAddonDisplay,
    showsAddon: item?.showsAddon ?? null,
    createdAt: item?.createdAt ?? null,
    preparationTime: item?.preparationTime ?? item?.PreparationTime ?? item?.preparation_time ?? item?.prepTime ?? null,
    cookTimeSeconds: item?.cookTimeSeconds ?? null,
    cookStartDelaySeconds: item?.cookStartDelaySeconds ?? null,
    isSeparator: !!item?.isCourseSeparator,
    isMessage: !!item?.isMessage,
    isFireItem: !!item?.isFireItem,
    status: item?.status ?? null,
    isBumped: !!item?.isBumped,
    isFired: !!item?.isFired
  });
  const logCourseSync = (label, payload, ...rest) => {
    return;
  };
  const logAddonDupCourse = (label, payload = {}) => {
    return;
  };
  const normalizeStoredUtcDateTime = value => {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }
    const raw = String(value).trim();
    if (!raw) return null;
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)) {
      return `${raw.replace(" ", "T")}Z`;
    }
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(raw)) {
      return `${raw}Z`;
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? raw : parsed.toISOString();
  };

  // already added earlier:
  const ordersRef = useRef(orders);
  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);
  function findOrder({
    ticket_instance_id,
    order_id
  }) {
    const nTicket = ticket_instance_id ? normalizeTicket(ticket_instance_id) : null;
    return ordersRef.current.find(o => {
      const byTicket = nTicket && normalizeTicket(o.ticket_instance_id) === nTicket;
      const byOrder = order_id != null && String(o.order_id) === String(order_id);
      return byTicket || byOrder;
    }) || null;
  }
  function findItemInOrder(order, item_id) {
    if (!order) return null;
    const needle = normalizeItem(item_id);
    return order.items?.find(i => normalizeItem(i.ID) === needle) || null;
  }
  function isRenderableFireRow(item) {
    return !!item && !item.isCourseSeparator && !item.isMessage && !item.isFireItem;
  }
  function isVoidedItem(item) {
    const status = String(item?.status || "").toLowerCase();
    return !!item?.isVoided || status === "voided" || Number(item?.TransType) === 2;
  }
  function isTicketFullyBumpedForDisplay(items = []) {
    const realItems = (Array.isArray(items) ? items : []).filter(isRenderableFireRow);
    return realItems.length > 0 && realItems.every(item => item?.isBumped || isVoidedItem(item));
  }
  function hasTrackedBumpForTicket(ticket_instance_id, order_id = null) {
    const needle = normalizeTicket(ticket_instance_id);
    if (!needle) return false;
    for (const key of bumpTicketEmittersRef.current.keys()) {
      if (normalizeTicket(key) === needle) return true;
    }
    if (bumpedTicketHistoryRef.current.has(needle)) return true;
    if (order_id != null && bumpedOrderHistoryRef.current.has(String(order_id))) return true;
    return false;
  }
  function wasTicketPreviouslyBumpedForDisplay(ticket_instance_id, existingItems = [], order_id = null) {
    const visibleItems = Array.isArray(existingItems) ? existingItems : [];
    if (visibleItems.length > 0) {
      const wasFullyBumped = isTicketFullyBumpedForDisplay(visibleItems);
      return wasFullyBumped;
    }
    const trackedBump = hasTrackedBumpForTicket(ticket_instance_id, order_id);
    return trackedBump;
  }
  function reopenTicketItemForDisplay(item, reopenAt, options = {}) {
    const {
      suppressAddonDisplay = false,
      forceNonAddon = false
    } = options;
    const nextItem = {
      ...item,
      createdAt: reopenAt,
      suppressAddonDisplay: !!suppressAddonDisplay
    };
    if (forceNonAddon) {
      nextItem.is_addon = 0;
      nextItem.isAddon = false;
      nextItem.isaddon = 0;
      nextItem.showsAddon = false;
      nextItem.suppressAddonDisplay = false;
    }
    if (!isVoidedItem(nextItem)) {
      nextItem.isBumped = false;
      nextItem.bumpedAt = null;
      nextItem.bumped_at = null;
      nextItem.status = "active";
    }
    return nextItem;
  }
  function normalizeTicketFirePromotionMode(value) {
    const mode = String(value || "none").trim().toLowerCase();
    return mode === "any_fired" || mode === "all_fired" ? mode : "none";
  }
  function shouldPromoteTicketForFire(order, mode) {
    const normalizedMode = normalizeTicketFirePromotionMode(mode);
    if (normalizedMode === "none" || !order) return false;
    const realItems = (order.items || []).filter(isRenderableFireRow);
    if (normalizedMode === "any_fired") {
      return realItems.some(item => item?.isFired === true);
    }
    const activeItems = realItems.filter(item => !item?.isBumped && !isVoidedItem(item));
    return activeItems.length > 0 && activeItems.every(item => item?.isFired === true);
  }
  function requestTicketFirePromotion(order) {
    if (!order) {
      return;
    }
    const mode = normalizeTicketFirePromotionMode(settingsRef.current?.ticket_fire_promotion_mode);
    const qualifies = shouldPromoteTicketForFire(order, mode);
    const realItems = (order.items || []).filter(isRenderableFireRow);
    const activeItems = realItems.filter(item => !item?.isBumped && !isVoidedItem(item));
    if (typeof onTicketFirePromotionRequested !== "function") {
      return;
    }
    if (!qualifies) return;
    onTicketFirePromotionRequested({
      mode,
      ticket_instance_id: order.ticket_instance_id ?? null,
      order_id: order.order_id ?? null,
      card: Number.isFinite(Number(order?.card)) ? Number(order.card) : 0
    });
  }
  function requestUnparkTicketWhenItemsAreFired(order) {
    if (settingsRef.current?.unpark_ticket_when_items_are_fired !== true) return;
    if (!order?.isParked || !order?.ticket_instance_id) return;
    const ticketId = String(order.ticket_instance_id);
    if (pendingFireUnparksRef.current.has(ticketId)) return;
    pendingFireUnparksRef.current.add(ticketId);
    unparkTicketRef.current(order.ticket_instance_id, { source: "fire-local" });
  }
  function buildProjectedFireItemOrder(order, itemId) {
    if (!order || itemId == null) return null;
    const targetId = String(itemId);
    let changed = false;
    const now = new Date().toISOString();
    const items = (order.items || []).map(item => {
      if (String(item.ID) === targetId && !item.isFired && !item.isBumped && !item.isCourseSeparator && !item.isMessage && !item.isFireItem) {
        changed = true;
        return {
          ...item,
          isFired: true,
          firedAt: now
        };
      }
      return item;
    });
    return changed ? {
      ...order,
      items
    } : null;
  }
  function buildProjectedFireCourseOrder(order, courseNumber) {
    if (!order || !Number.isFinite(Number(courseNumber))) return null;
    const targetCourse = Number(courseNumber);
    let changed = false;
    const now = new Date().toISOString();
    const items = (order.items || []).map(item => {
      if (Number(item.course) === targetCourse && !item.isFired && !item.isBumped && !item.isCourseSeparator && !item.isMessage && !item.isFireItem) {
        changed = true;
        return {
          ...item,
          isFired: true,
          firedAt: now
        };
      }
      return item;
    });
    return changed ? {
      ...order,
      items
    } : null;
  }
  function buildProjectedFireAllItemsOrder(order) {
    if (!order) return null;
    let changed = false;
    const now = new Date().toISOString();
    const items = (order.items || []).map(item => {
      if (!item.isFired && !item.isBumped && !item.isCourseSeparator && !item.isMessage && !item.isFireItem) {
        changed = true;
        return {
          ...item,
          isFired: true,
          firedAt: now
        };
      }
      return item;
    });
    return changed ? {
      ...order,
      items
    } : null;
  }
  function applyParkStateToOrders(prev, {
    ticket_instance_id,
    order_id,
    parkedAt = null,
    unparkTime = undefined,
    isParked
  }) {
    const normalizedTicket = ticket_instance_id ? normalizeTicket(ticket_instance_id) : null;
    const normalizedOrderId = order_id != null ? String(order_id) : null;
    const parkStamp = parkedAt || new Date().toISOString();
    return prev.map(order => {
      const sameTicket = normalizedTicket && normalizeTicket(order?.ticket_instance_id) === normalizedTicket;
      const sameOrder = normalizedOrderId != null && String(order?.order_id ?? "") === normalizedOrderId;
      if (!sameTicket && !sameOrder) return order;
      const orderStatus = String(order?.status || "").toLowerCase();
      if (isParked && (orderStatus === "voided" || orderStatus === "bumped")) {
        return order;
      }
      if (!isParked && orderStatus !== "parked" && !order?.isParked) {
        return order;
      }
      return {
        ...order,
        isParked: !!isParked,
        parkedAt: isParked ? order?.parkedAt || parkStamp : null,
        unparkTime: isParked ? unparkTime !== undefined ? unparkTime : order?.unparkTime ?? null : null,
        status: isParked ? "parked" : "pending",
        items: (order.items || []).map(item => {
          const itemStatus = String(item?.status || "").toLowerCase();
          const isVoided = item?.isVoided || itemStatus === "voided" || Number(item?.TransType) === 2;
          const isBumped = !!item?.isBumped || itemStatus === "bumped";
          if (isParked) {
            if (isVoided || isBumped) {
              return {
                ...item,
                isParked: false,
                parkedAt: null
              };
            }
            return {
              ...item,
              isParked: true,
              parkedAt: item?.parkedAt || parkStamp,
              totalPausedSeconds: item?.totalPausedSeconds || 0,
              status: "parked"
            };
          }
          if (itemStatus !== "parked" && !item?.isParked) return item;
          return {
            ...item,
            isParked: false,
            parkedAt: null,
            status: "active"
          };
        })
      };
    });
  }
  function getLastRealCourseFromCurrentOrder(orderId, ticketInstanceId) {
    const nTicket = ticketInstanceId ? normalizeTicket(ticketInstanceId) : null;
    const target = ordersRef.current.find(o => {
      const byOrder = orderId != null && String(o?.order_id ?? "") === String(orderId);
      const byTicket = nTicket && normalizeTicket(o?.ticket_instance_id) === nTicket;
      return byOrder || byTicket;
    });
    if (!target) return null;
    const nums = (target.items || []).filter(i => !i?.isCourseSeparator && !i?.isMessage && !i?.isFireItem).map(i => Number(i?.course)).filter(n => Number.isFinite(n));
    if (!nums.length) return null;
    return Math.max(...nums);
  }
  function buildOrderSummaryLike(o) {
    if (!o) return null;
    const items = Array.isArray(o.items) ? o.items : Array.isArray(o.Item) ? o.Item : [];
    const itemNames = items.filter(Boolean).map(i => i.Name).filter(Boolean);
    return {
      table_name: o.table_name ?? o.GuestTable ?? null,
      destination: o.destination ?? o.Destination ?? null,
      server_name: o.server_name ?? o.ServerName ?? null,
      covers: o.covers ?? null,
      item_count: itemNames.length,
      item_names_preview: itemNames.slice(0, 8)
    };
  }
  function getHandledSetForTicket(ticketInstanceId) {
    const key = String(ticketInstanceId).split("-")[0]; // normalize
    let set = handledMsgByTicketRef.current.get(key);
    if (!set) {
      set = new Set();
      handledMsgByTicketRef.current.set(key, set);
    }
    return set;
  }
  // --- order-received echo/de-dupe/throttle ---
  const lastOrderReceivedRef = useRef(new Map()); // key -> timestamp

  function orderKey(o) {
    const tid = o?.ticket_instance_id ? String(o.ticket_instance_id).split("-")[0] : null;
    const oid = o?.order_id ?? o?.ID ?? null;
    return tid || (oid != null ? `oid:${oid}` : null);
  }
  function shouldEmitOrderReceived(o) {
    // never echo someone else’s emit
    if (o?.sender_screen_id && o.sender_screen_id !== mountIdRef.current) return false;
    const k = orderKey(o);
    if (!k) return true;
    const now = Date.now();
    const last = Number(lastOrderReceivedRef.current.get(k) || 0);
    const THROTTLE_MS = 5000; // per-ticket debounce
    if (now - last < THROTTLE_MS) return false;
    lastOrderReceivedRef.current.set(k, now);
    return true;
  }

  // pull S* messages off a POS-style OrderMessages object
  function extractTicketMessages(msgObj = {}) {
    return Object.entries(msgObj).filter(([k]) => String(k).startsWith("S")).map(([, v]) => String(v).trim()).filter(Boolean);
  }

  // "Covers: 4" (first match wins)
  function extractCoversFromMessagesList(list = []) {
    const m = list.find(s => /^covers:\s*/i.test(String(s)));
    return m || null;
  }
  function shouldApplyRemoteParkingEvent(data = {}) {
    const mirroredStationIds = Array.isArray(data?.mirrored_station_ids) ? data.mirrored_station_ids.map(value => Number(value)).filter(value => Number.isFinite(value)) : [];
    if (mirroredStationIds.length > 0) {
      return mirroredStationIds.includes(Number(stationId));
    }
    const current = settingsRef.current || {};
    const method = String(current?.parking_sync_method || (current?.sync_parking_with_all_stations ? "all_stations" : current?.sync_parking_with_expo ? "expo" : "none")).trim().toLowerCase();
    if (method === "all_stations") return true;
    if (method === "expo" && String(data?.sender_role || "").trim().toLowerCase() === "expo") {
      return true;
    }
    return false;
  }
  function shouldApplyRemoteFireEvent(data = {}) {
    const mirroredStationIds = Array.isArray(data?.mirrored_station_ids) ? data.mirrored_station_ids.map(value => Number(value)).filter(value => Number.isFinite(value)) : [];
    if (mirroredStationIds.length > 0) {
      return mirroredStationIds.includes(Number(stationId));
    }
    const current = settingsRef.current || {};
    const method = String(current?.firing_sync_method || (current?.sync_firing_with_all_stations ? "all_stations" : current?.sync_firing_with_expo ? "expo" : "none")).trim().toLowerCase();
    if (method === "all_stations") return true;
    if (method === "expo" && String(data?.sender_role || "").trim().toLowerCase() === "expo") {
      return true;
    }
    return false;
  }
  function getMirroredBumpStationIds(data = {}) {
    return Array.isArray(data?.mirrored_station_ids) ? data.mirrored_station_ids.map(value => Number(value)).filter(value => Number.isFinite(value)) : [];
  }
  function shouldApplyMirroredBumpSyncEvent(data = {}) {
    return getMirroredBumpStationIds(data).includes(Number(stationId));
  }
  function isDisplayOnlyBumpMode() {
    return settingsRef.current?.display_bump_status_of_other_units === true;
  }
  function scheduleDisplayOnlyRefresh() {
    if (!isDisplayOnlyBumpMode()) return;
    if (displayOnlyRefreshTimerRef.current) {
      clearTimeout(displayOnlyRefreshTimerRef.current);
    }
    displayOnlyRefreshTimerRef.current = setTimeout(() => {
      displayOnlyRefreshTimerRef.current = null;
      fetchOrders();
    }, 350);
  }
  function scheduleRemoteStateRefresh() {
    if (remoteStateRefreshTimerRef.current) {
      clearTimeout(remoteStateRefreshTimerRef.current);
    }
    remoteStateRefreshTimerRef.current = setTimeout(() => {
      remoteStateRefreshTimerRef.current = null;
      logKdsActivity("kds.refresh_requested", { reason: "remote_bump_sync" });
      fetchOrders();
    }, 200);
  }
  function getDisplayBumpStationIds(data = {}) {
    const stationIds = [data?.sender_station_id ?? data?.station_id];
    return Array.from(new Set(stationIds.map(value => Number(value)).filter(value => Number.isFinite(value))));
  }
  function getItemDisplayBumpStationIds(data = {}) {
    const mirroredStationIds = Array.isArray(data?.mirrored_station_ids) ? data.mirrored_station_ids : [];
    const stationIds = [data?.sender_station_id ?? data?.station_id, ...mirroredStationIds];
    return Array.from(new Set(stationIds.map(value => Number(value)).filter(value => Number.isFinite(value)))).sort((a, b) => a - b);
  }
  function getCompletedCourseStationIds(data = {}) {
    const explicitStationIds = Array.isArray(data?.course_completed_station_ids) ? data.course_completed_station_ids : [];
    const stationIds = explicitStationIds.length ? explicitStationIds : [data?.sender_station_id ?? data?.station_id];
    return Array.from(new Set(stationIds.map(value => Number(value)).filter(value => Number.isFinite(value)))).sort((a, b) => a - b);
  }
  function patchTicketDisplayBox(order, senderStationId, patch = {}) {
    const stationIdNum = Number(senderStationId);
    if (!Number.isFinite(stationIdNum)) return order;
    const existingBoxes = Array.isArray(order?.ticket_bump_display_boxes) ? order.ticket_bump_display_boxes : [];
    const stationIds = Array.from(new Set([...existingBoxes.map(box => Number(box?.station_id)), stationIdNum].filter(value => Number.isFinite(value)))).sort((a, b) => a - b);
    if (!stationIds.length) return order;
    const boxes = stationIds.map(currentStationId => {
      const existingBox = existingBoxes.find(box => Number(box?.station_id) === currentStationId) || null;
      return existingBox || {
        station_id: currentStationId,
        is_self: Number(currentStationId) === Number(stationId),
        received: false,
        bumped: false
      };
    });
    let changed = false;
    const nextBoxes = boxes.map(box => {
      if (Number(box?.station_id) !== stationIdNum) return box;
      changed = true;
      return {
        ...box,
        ...patch
      };
    });
    return changed ? {
      ...order,
      ticket_bump_display_boxes: nextBoxes
    } : order;
  }
  function patchCourseDisplayBoxes(order, course, stationIdsToBump = [], bumped = true) {
    const courseKey = String(course);
    const currentCourseBoxes = order && order.course_bump_display_boxes && typeof order.course_bump_display_boxes === "object" ? order.course_bump_display_boxes : {};
    const existingBoxes = Array.isArray(currentCourseBoxes?.[courseKey]) ? currentCourseBoxes[courseKey] : [];
    const completedStationIds = new Set((Array.isArray(stationIdsToBump) ? stationIdsToBump : []).map(value => Number(value)).filter(value => Number.isFinite(value)));
    const courseStationIds = Array.from(new Set([...existingBoxes.map(box => Number(box?.station_id)).filter(value => Number.isFinite(value)), ...Array.from(completedStationIds)])).sort((a, b) => a - b);
    if (!courseStationIds.length) return order;
    let changed = false;
    const nextBoxes = courseStationIds.map(stationIdNum => {
      const box = existingBoxes.find(entry => Number(entry?.station_id) === stationIdNum) || {
        station_id: stationIdNum,
        received: false,
        bumped: false
      };
      const nextBumped = completedStationIds.has(stationIdNum) ? !!bumped : false;
      const nextReceived = box?.received === true || completedStationIds.has(stationIdNum);
      if (box?.bumped === nextBumped && box?.received === nextReceived) return box;
      changed = true;
      return {
        ...box,
        received: nextReceived,
        bumped: nextBumped
      };
    });
    if (!changed) return order;
    return {
      ...order,
      course_bump_display_boxes: {
        ...currentCourseBoxes,
        [courseKey]: nextBoxes
      }
    };
  }
  function isDisplayBumpableItem(item = {}) {
    const status = String(item?.status || "").toLowerCase();
    return !(item?.isCourseSeparator || item?.isMessage || item?.isFireItem || item?.isVoided || Number(item?.TransType) === 2 || status === "voided");
  }
  function recomputeCourseDisplayBoxes(order) {
    const items = Array.isArray(order?.items) ? order.items : [];
    const currentCourseBoxes = order && order.course_bump_display_boxes && typeof order.course_bump_display_boxes === "object" ? order.course_bump_display_boxes : {};
    const nextCourseBoxes = {};
    const stationIds = Array.from(new Set([...Object.values(currentCourseBoxes).flatMap(boxes => Array.isArray(boxes) ? boxes : []).map(box => Number(box?.station_id)).filter(stationId => Number.isFinite(stationId)), ...items.flatMap(item => Array.isArray(item?.item_bump_display_boxes) ? item.item_bump_display_boxes : []).map(box => Number(box?.station_id)).filter(stationId => Number.isFinite(stationId))])).sort((a, b) => a - b);
    const courseNumbers = Array.from(new Set(items.map(item => Number(item?.course)).filter(courseNum => Number.isFinite(courseNum)))).sort((a, b) => a - b);
    for (const courseNum of courseNumbers) {
      const courseItems = items.filter(item => Number(item?.course) === courseNum && !item?.isCourseSeparator && !item?.isMessage);
      nextCourseBoxes[String(courseNum)] = stationIds.map(stationIdNum => {
        const previousBox = currentCourseBoxes?.[String(courseNum)]?.find(box => Number(box?.station_id) === stationIdNum) || null;
        const stationItems = courseItems.filter(item => {
          const itemBoxes = Array.isArray(item?.item_bump_display_boxes) ? item.item_bump_display_boxes : [];
          return itemBoxes.some(itemBox => Number(itemBox?.station_id) === stationIdNum && itemBox?.received);
        });
        const bumpableStationItems = stationItems.filter(isDisplayBumpableItem);
        const bumped = stationItems.length > 0 && bumpableStationItems.length > 0 && bumpableStationItems.every(item => (item.item_bump_display_boxes || []).some(itemBox => Number(itemBox?.station_id) === stationIdNum && itemBox?.received && itemBox?.bumped));
        return {
          ...previousBox,
          station_id: stationIdNum,
          received: stationItems.length > 0,
          bumped
        };
      });
    }
    return nextCourseBoxes;
  }
  function patchItemDisplayBoxes(order, senderStationId, itemId, patch = {}, options = {}) {
    const stationIdNum = Number(senderStationId);
    const itemNeedle = normalizeItem(itemId);
    const createIfMissing = options?.createIfMissing === true;
    const requireReceived = options?.requireReceived === true;
    const recomputeCourseBoxes = options?.recomputeCourseBoxes !== false;
    if (!Number.isFinite(stationIdNum) || !itemNeedle) return order;
    let changed = false;
    const nextItems = (order?.items || []).map(item => {
      if (normalizeItem(item?.ID) !== itemNeedle) return item;
      const existingBoxes = Array.isArray(item?.item_bump_display_boxes) && item.item_bump_display_boxes.length ? item.item_bump_display_boxes : [];
      const existingStationIds = existingBoxes.map(box => Number(box?.station_id)).filter(value => Number.isFinite(value));
      const hasStationBox = existingStationIds.includes(stationIdNum);
      if (!hasStationBox && !createIfMissing) return item;
      const stationIds = Array.from(new Set([...existingStationIds, ...(createIfMissing ? [stationIdNum] : [])].filter(value => Number.isFinite(value)))).sort((a, b) => a - b);
      const boxes = stationIds.map(currentStationId => {
        const existingBox = existingBoxes.find(box => Number(box?.station_id) === currentStationId) || null;
        return existingBox || {
          station_id: currentStationId,
          is_self: Number(currentStationId) === Number(stationId),
          received: false,
          bumped: false
        };
      });
      if (!boxes.length) return item;
      const nextBoxes = boxes.map(box => {
        if (Number(box?.station_id) !== stationIdNum) return box;
        if (requireReceived && box?.received !== true) return box;
        changed = true;
        return {
          ...box,
          ...patch
        };
      });
      return changed ? {
        ...item,
        item_bump_display_boxes: nextBoxes
      } : item;
    });
    if (!changed) return order;
    const nextOrder = {
      ...order,
      items: nextItems
    };
    return recomputeCourseBoxes ? {
      ...nextOrder,
      course_bump_display_boxes: recomputeCourseDisplayBoxes(nextOrder)
    } : nextOrder;
  }
  function applyTicketDisplayReceiptState(prev, data = {}) {
    const orderNeedle = data?.order_id != null ? String(data.order_id) : null;
    const ticketNeedle = data?.ticket_instance_id ? normalizeTicket(data.ticket_instance_id) : null;
    const senderStationId = data?.sender_station_id ?? data?.station_id;
    return prev.map(order => {
      const sameOrder = orderNeedle != null && String(order?.order_id ?? "") === orderNeedle;
      const sameTicket = ticketNeedle && normalizeTicket(order?.ticket_instance_id) === ticketNeedle;
      if (!sameOrder && !sameTicket) return order;
      const nextOrder = patchTicketDisplayBox(order, senderStationId, {
        received: true
      });
      return Array.isArray(data?.item_id) ? nextOrder : patchItemDisplayBoxes(nextOrder, senderStationId, data?.item_id, {
        received: true
      }, {
        createIfMissing: true
      });
    });
  }
  function applyTicketDisplayBumpState(prev, data = {}, bumped) {
    const orderNeedle = data?.order_id != null ? String(data.order_id) : null;
    const ticketNeedle = data?.ticket_instance_id ? normalizeTicket(data.ticket_instance_id) : null;
    const stationIds = getDisplayBumpStationIds(data);
    return prev.map(order => {
      const sameOrder = orderNeedle != null && String(order?.order_id ?? "") === orderNeedle;
      const sameTicket = ticketNeedle && normalizeTicket(order?.ticket_instance_id) === ticketNeedle;
      if (!sameOrder && !sameTicket) return order;
      let nextOrder = order;
      for (const stationIdNum of stationIds) {
        nextOrder = patchTicketDisplayBox(nextOrder, stationIdNum, {
          bumped: !!bumped
        });
      }
      return nextOrder;
    });
  }
  function applyItemDisplayBumpState(prev, data = {}, bumped) {
    const orderNeedle = data?.order_id != null ? String(data.order_id) : null;
    const ticketNeedle = data?.ticket_instance_id ? normalizeTicket(data.ticket_instance_id) : null;
    const stationIds = getItemDisplayBumpStationIds(data);
    const itemNeedle = data?.item_id != null ? normalizeItem(data.item_id) : null;
    if (!itemNeedle) return prev;
    return prev.map(order => {
      const sameOrder = orderNeedle != null && String(order?.order_id ?? "") === orderNeedle;
      const sameTicket = ticketNeedle && normalizeTicket(order?.ticket_instance_id) === ticketNeedle;
      if (!sameOrder && !sameTicket) return order;
      let nextOrder = order;
      for (const stationIdNum of stationIds) {
        nextOrder = patchItemDisplayBoxes(nextOrder, stationIdNum, itemNeedle, {
          bumped: !!bumped
        }, {
          requireReceived: true,
          recomputeCourseBoxes: true
        });
      }
      return nextOrder;
    });
  }
  function applyCourseDisplayBumpState(prev, data = {}, bumped) {
    const orderNeedle = data?.order_id != null ? String(data.order_id) : null;
    const ticketNeedle = data?.ticket_instance_id ? normalizeTicket(data.ticket_instance_id) : null;
    const stationIds = getCompletedCourseStationIds(data);
    const courseNeedle = Number(data?.course);
    if (!Number.isFinite(courseNeedle)) return prev;
    return prev.map(order => {
      const sameOrder = orderNeedle != null && String(order?.order_id ?? "") === orderNeedle;
      const sameTicket = ticketNeedle && normalizeTicket(order?.ticket_instance_id) === ticketNeedle;
      if (!sameOrder && !sameTicket) return order;
      return patchCourseDisplayBoxes(order, courseNeedle, stationIds, bumped);
    });
  }
  function applyRemoteBumpItemState(prev, data = {}) {
    const ticketNeedle = data?.ticket_instance_id ? normalizeTicket(data.ticket_instance_id) : null;
    const orderNeedle = data?.order_id != null ? String(data.order_id) : null;
    const itemNeedle = data?.item_id != null ? normalizeItem(data.item_id) : null;
    if (!itemNeedle) return prev;
    return prev.map(order => {
      const sameTicket = ticketNeedle && normalizeTicket(order?.ticket_instance_id) === ticketNeedle;
      const sameOrder = orderNeedle != null && String(order?.order_id ?? "") === orderNeedle;
      if (!sameTicket && !sameOrder) return order;
      let nextOrderForBoxes = order;
      for (const stationIdNum of getItemDisplayBumpStationIds(data)) {
        nextOrderForBoxes = patchItemDisplayBoxes(nextOrderForBoxes, stationIdNum, data?.item_id, {
          bumped: data?.isBumped !== false
        }, {
          requireReceived: true,
          recomputeCourseBoxes: true
        });
      }
      return {
        ...nextOrderForBoxes,
        items: (nextOrderForBoxes.items || []).map(item => {
          if (normalizeItem(item?.ID) !== itemNeedle) return item;
          const itemStatus = String(item?.status || "").toLowerCase();
          const isVoided = item?.isVoided || itemStatus === "voided" || Number(item?.TransType) === 2;
          const isBumped = data?.isBumped !== false;
          if (isVoided) return item;
          return {
            ...item,
            isBumped,
            status: isBumped ? "bumped" : "active",
            bumpedAt: isBumped ? data?.bumpedAt ?? item?.bumpedAt ?? new Date().toISOString() : null
          };
        })
      };
    });
  }
  function applyRemoteBumpCourseState(prev, data = {}) {
    const ticketNeedle = data?.ticket_instance_id ? normalizeTicket(data.ticket_instance_id) : null;
    const orderNeedle = data?.order_id != null ? String(data.order_id) : null;
    const courseNeedle = Number(data?.course);
    const explicitItemIds = Array.isArray(data?.course_item_ids) ? new Set(data.course_item_ids.map(id => normalizeItem(id)).filter(Boolean)) : null;
    if (!Number.isFinite(courseNeedle)) return prev;
    return prev.map(order => {
      const sameTicket = ticketNeedle && normalizeTicket(order?.ticket_instance_id) === ticketNeedle;
      const sameOrder = orderNeedle != null && String(order?.order_id ?? "") === orderNeedle;
      if (!sameTicket && !sameOrder) return order;
      const itemDisplayStationIds = getItemDisplayBumpStationIds(data);
      const completedStationIds = getCompletedCourseStationIds(data);
      const nextOrderForBoxes = {
        ...order
      };
      const nextItems = (order.items || []).map(item => {
        const itemStatus = String(item?.status || "").toLowerCase();
        const isVoided = item?.isVoided || itemStatus === "voided" || Number(item?.TransType) === 2;
        if (isVoided || item?.isCourseSeparator || item?.isMessage || item?.isFireItem || Number(item?.course) !== courseNeedle || explicitItemIds && !explicitItemIds.has(normalizeItem(item?.ID))) {
          return item;
        }
        const nextItem = {
          ...item,
          isBumped: true,
          status: "bumped",
          bumpedAt: data?.bumpedAt ?? item?.bumpedAt ?? new Date().toISOString()
        };
        let patchedItem = nextItem;
        for (const stationIdNum of itemDisplayStationIds) {
          patchedItem = patchItemDisplayBoxes({
            items: [patchedItem]
          }, stationIdNum, patchedItem?.ID, {
            bumped: true
          }, {
            requireReceived: true,
            recomputeCourseBoxes: false
          }).items[0];
        }
        return patchedItem;
      });
      nextOrderForBoxes.items = nextItems;
      return patchCourseDisplayBoxes(nextOrderForBoxes, courseNeedle, completedStationIds, true);
    });
  }
  function applyRemoteBumpTicketState(prev, data = {}) {
    const ticketNeedle = data?.ticket_instance_id ? normalizeTicket(data.ticket_instance_id) : null;
    const orderNeedle = data?.order_id != null ? String(data.order_id) : null;
    return prev.filter(order => {
      const sameTicket = ticketNeedle && normalizeTicket(order?.ticket_instance_id) === ticketNeedle;
      const sameOrder = orderNeedle != null && String(order?.order_id ?? "") === orderNeedle;
      return !sameTicket && !sameOrder;
    });
  }

  // On component mount (and when the station ID changes), fetch the KDS settings
  // from the backend for the current station and store them in local state.
  useEffect(() => {
    async function fetchSettings() {
      try {
        const res = await apiFetch(`/settings/kds_settings?station_id=${stationId}`);
        const data = await res.json();

        // Normalize string booleans to real booleans
        const normalizeBool = val => val === true || val === "true";
        const parseIdList = value => {
          if (Array.isArray(value)) {
            return Array.from(new Set(value.map(entry => Number(entry)).filter(entry => Number.isInteger(entry) && entry > 0))).sort((a, b) => a - b);
          }
          if (value == null || value === "") return [];
          try {
            return parseIdList(JSON.parse(value));
          } catch {
            return [];
          }
        };
        const normalized = {
          ...data,
          is_encore: normalizeBool(data.is_encore ?? false),
          enable_printing_from_ods: normalizeBool(data.enable_printing_from_ods ?? false),
          print_ticket_on_bump: normalizeBool(data.print_ticket_on_bump ?? false),
          print_course_on_bump: normalizeBool(data.print_course_on_bump ?? false),
          selected_printer_ids: parseIdList(data.selected_printer_ids),
          show_category: normalizeBool(data.show_category),
          show_seat_number: normalizeBool(data.show_seat_number ?? true),
          group_by_category: normalizeBool(data.group_by_category),
          combine_condiments: normalizeBool(data.combine_condiments ?? true),
          item_prep_display_mode: ["normal", "darkened", "hidden"].includes(String(data.item_prep_display_mode || "").toLowerCase()) ? String(data.item_prep_display_mode).toLowerCase() : "normal",
          enable_fire_timer: normalizeBool(data.enable_fire_timer ?? true),
          auto_fire_from_expo: (data.auto_fire_from_expo === true || data.auto_fire_from_expo === 'true') ?? true,
          firing_sync_method: String(data.firing_sync_method || (data.sync_firing_with_all_stations === true || data.sync_firing_with_all_stations === "true" ? "all_stations" : data.sync_firing_with_expo === true || data.sync_firing_with_expo === "true" ? "expo" : "none")),
          sync_firing_with_expo: normalizeBool(data.sync_firing_with_expo ?? false),
          sync_firing_with_all_stations: normalizeBool(data.sync_firing_with_all_stations ?? false),
          bumping_sync_method: String(data.bumping_sync_method || "none"),
          parking_sync_method: String(data.parking_sync_method || (data.sync_parking_with_all_stations === true || data.sync_parking_with_all_stations === "true" ? "all_stations" : data.sync_parking_with_expo === true || data.sync_parking_with_expo === "true" ? "expo" : "none")),
          ticket_location_sync_method: String(data.ticket_location_sync_method || "none"),
          ticket_page_sync_method: String(data.ticket_page_sync_method || "none"),
          ticket_fire_promotion_mode: normalizeTicketFirePromotionMode(data.ticket_fire_promotion_mode),
          enable_freeform_board: normalizeBool(data.enable_freeform_board ?? false),
          enable_line_item_board: normalizeBool(data.enable_line_item_board ?? false),
          default_order_destination: ["normal", "freeform", "line_items"].includes(String(data.default_order_destination || "").toLowerCase()) ? String(data.default_order_destination).toLowerCase() : "normal",
          park_ticket_on_course_bump: normalizeBool(data.park_ticket_on_course_bump ?? false),
          unpark_ticket_when_items_are_fired: normalizeBool(data.unpark_ticket_when_items_are_fired ?? false),
          enable_bump_bar: normalizeBool(data.enable_bump_bar ?? false),
          display_bump_status_of_other_units: normalizeBool(data.display_bump_status_of_other_units ?? false),
          display_bump_status_other_units_ticket: normalizeBool(data.display_bump_status_other_units_ticket ?? true),
          display_bump_status_other_units_course: normalizeBool(data.display_bump_status_other_units_course ?? false),
          display_bump_status_other_units_item: normalizeBool(data.display_bump_status_other_units_item ?? false),
          display_course_labels: normalizeBool(data.display_course_labels ?? true),
          course_labels: (() => {
            try {
              const parsed = typeof data.course_labels === "string" ? JSON.parse(data.course_labels) : data.course_labels;
              return parsed && typeof parsed === "object" ? parsed : {};
            } catch {
              return {};
            }
          })(),
          coursing: normalizeBool(data.coursing ?? false),
          sync_courses_with_expo: normalizeBool(data.sync_courses_with_expo ?? false),
          hide_course: normalizeBool(data.hide_course ?? false),
          hide_bumped_courses: normalizeBool(data.hide_bumped_courses ?? false),
          show_detailed_timing_data: normalizeBool(data.show_detailed_timing_data ?? true),
          ticket_color_by_course: normalizeBool(data.ticket_color_by_course ?? true),
          use_course_separator: normalizeBool(data.use_course_separator),
          course_separator_top: normalizeBool(data.course_separator_top ?? false),
          separate_itempreps_in_summary: normalizeBool(data.separate_itempreps_in_summary ?? true),
          hide_preps_in_summary: normalizeBool(data.hide_preps_in_summary ?? false)
        };
        logCourseSync("settings-loaded", {
          coursing: normalized.coursing,
          sync_courses_with_expo: normalized.sync_courses_with_expo,
          firing_sync_method: normalized.firing_sync_method,
          sync_firing_with_expo: normalized.sync_firing_with_expo,
          sync_firing_with_all_stations: normalized.sync_firing_with_all_stations,
          bumping_sync_method: normalized.bumping_sync_method,
          parking_sync_method: normalized.parking_sync_method,
          ticket_location_sync_method: normalized.ticket_location_sync_method,
          ticket_page_sync_method: normalized.ticket_page_sync_method,
          ticket_fire_promotion_mode: normalized.ticket_fire_promotion_mode,
          sync_parking_with_expo: normalizeBool(data.sync_parking_with_expo ?? false),
          sync_parking_with_all_stations: normalizeBool(data.sync_parking_with_all_stations ?? false),
          use_course_separator: normalized.use_course_separator,
          group_by_category: normalized.group_by_category
        });
        logKdsActivity("kds.color_settings_loaded", {
          ticket_color_yellow_after: normalized.ticket_color_yellow_after ?? null,
          ticket_color_red_after: normalized.ticket_color_red_after ?? null,
          course_color_yellow_after: normalized.course_color_yellow_after ?? null,
          course_color_red_after: normalized.course_color_red_after ?? null,
          ticket_color_by_course: normalized.ticket_color_by_course,
          coursing: normalized.coursing,
        });
        setSettings(normalized);
      } catch (err) {
        console.error("Failed to load settings:", err);
      }
    }
    fetchSettings();
  }, [stationId]);

  // Always keep a live ref to the most current settings object,
  // so that it can be accessed inside event handlers or closures.
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // --- Tracking refs ---
  const emittersRef = useRef(new Map());
  const itemReceiversRef = useRef(new Map());
  const fireTicketEmittersRef = useRef(new Map());
  const fireCourseEmittersRef = useRef(new Map());
  const fireItemEmittersRef = useRef(new Map());
  const bumpTicketEmittersRef = useRef(new Map());
  const bumpCourseEmittersRef = useRef(new Map());
  const bumpItemEmittersRef = useRef(new Map());
  const voidItemEmittersRef = useRef(new Map());
  const expoCourseByOrderItemRef = useRef(new Map()); // key: order_id::item_id -> { course, senderStationId }

  // This drives re-renders for consumers
  const [trackingVersion, setTrackingVersion] = useState(0);

  // Small helper to add to a Map<key, Set<string>> and bump the version
  function track(mapRef, key, payload = {}) {
    if (!key) return;
    if (!mapRef.current.has(key)) mapRef.current.set(key, new Set());

    // Keep all payload fields, but guarantee sender + timestamp
    const entry = {
      ...payload,
      sender_screen_id: payload.sender_screen_id ?? null,
      sender_role: payload.sender_role ?? null,
      sender_station_id: payload.sender_station_id ?? payload.sender_station ?? null,
      at: new Date().toISOString()
    };
    mapRef.current.get(key).add(JSON.stringify(entry));
    setTrackingVersion(v => v + 1);
  }

  // Helper: Map -> plain object of arrays (UI-friendly)
  const mapToArrayObject = m => {
    const out = {};
    m.forEach((set, k) => {
      out[k] = Array.from(set);
    });
    return out;
  };

  //Get user info for socket emits
  const getUserInfoFor = o => o?.user_info ?? o?.UserInfo ?? null;

  // Memoized snapshot consumers can render
  const trackingSnapshot = useMemo(() => ({
    emitters: mapToArrayObject(emittersRef.current),
    itemReceivers: mapToArrayObject(itemReceiversRef.current),
    fireTicket: mapToArrayObject(fireTicketEmittersRef.current),
    fireCourse: mapToArrayObject(fireCourseEmittersRef.current),
    fireItem: mapToArrayObject(fireItemEmittersRef.current),
    bumpTicket: mapToArrayObject(bumpTicketEmittersRef.current),
    bumpCourse: mapToArrayObject(bumpCourseEmittersRef.current),
    bumpItem: mapToArrayObject(bumpItemEmittersRef.current),
    voidItem: mapToArrayObject(voidItemEmittersRef.current),
    version: trackingVersion // optional: expose version for debugging
  }), [trackingVersion]);

  // Initialization and socket hookup
  useEffect(() => {
    if (!stationId) return;
    function isFromMe(payload) {
      return payload?.sender_screen_id === mountIdRef.current;
    }

    // Fetch existing orders
    fetchOrders();

    // --- Socket Handlers ---
    const handlers = {
      "new-order": (data = {}) => {
        if (isFromMe(data)) return;
        // Handle it locally
        handleNewOrder(data);
        if (!Array.isArray(data?.ticket_bump_display_boxes)) {
          scheduleDisplayOnlyRefresh("new-order-missing-boxes", data);
        }
        try {
          // Prefer the payload; if it was slim, look up hydrated order just added
          const lookup = data?.items?.length || data?.Item?.length ? data : ordersRef.current.find(o => String(o.ticket_instance_id).split("-")[0] === String(data.ticket_instance_id).split("-")[0]);
          const summary = buildOrderSummaryLike(lookup || data) || {};
          track(emittersRef, data.order_id ?? data.ID, {
            ...summary,
            ticket_instance_id: data.ticket_instance_id,
            kind: "new-order"
          });
        } catch {}
      },
      "order-received": (data = {}) => {
        if (isFromMe(data)) return;
        // mark so handleNewOrder won't bounce it back inside the throttle window
        const k = orderKey(data);
        if (k) lastOrderReceivedRef.current.set(k, Date.now());

        // build summary for the tracking pane
        let summary = buildOrderSummaryLike(data);
        if (!summary || !summary.item_count) {
          const found = ordersRef.current.find(o => String(o.order_id) === String(data.order_id));
          summary = buildOrderSummaryLike(found);
        }
        const trackKey = data.order_id ?? data.ID ?? data.ticket_instance_id ?? "unknown";
        track(emittersRef, trackKey, {
          ...data,
          ...(summary || {}),
          kind: "order-received"
        });
        setOrders(prev => applyTicketDisplayReceiptState(prev, data));

        // hydrate/merge locally without re-emitting (guard lives in handleNewOrder)
        handleNewOrder(data);
      },
      "park-ticket": (data = {}) => {
        if (isFromMe(data)) return;
        if (!shouldApplyRemoteParkingEvent(data)) return;
        setOrders(prev => applyParkStateToOrders(prev, {
          ticket_instance_id: data?.ticket_instance_id,
          order_id: data?.order_id,
          parkedAt: normalizeStoredUtcDateTime(data?.parkedAt),
          unparkTime: normalizeStoredUtcDateTime(data?.unpark_time),
          isParked: true
        }));
      },
      "unpark-ticket": (data = {}) => {
        if (isFromMe(data)) return;
        if (!shouldApplyRemoteParkingEvent(data)) return;
        setLastUnparkSignal({
          ticket_instance_id: data?.ticket_instance_id ?? null,
          order_id: data?.order_id ?? null,
          source: "remote-socket",
          nonce: Date.now()
        });
        setOrders(prev => applyParkStateToOrders(prev, {
          ticket_instance_id: data?.ticket_instance_id,
          order_id: data?.order_id,
          isParked: false
        }));
      },
      // used to keep track of what station received what items (for use in ui later on)
      "item-received": (data = {}) => {
        if (isFromMe(data)) return;
        const {
          order_id,
          item_id
        } = data;
        if (!order_id || !item_id) return;
        if (data?.addon_sync_ack === true) return;

        // Ensure uniqueness per order
        const key = `${order_id}::${item_id}`;

        // Try to fill item_name/category from current orders if missing
        let {
          item_name,
          category,
          course
        } = data;
        let {
          seat_number
        } = data;
        if (!item_name || !category || course == null) {
          const needle = String(item_id).split("-")[0];
          const matchOrder = ordersRef.current.find(o => String(o.order_id) === String(order_id));
          const matchItem = matchOrder?.items?.find(i => String(i.ID).split("-")[0] === needle);
          if (matchItem) {
            item_name = item_name ?? matchItem.Name;
            category = category ?? matchItem.Category;
            course = course == null ? matchItem.course : course;
            seat_number = seat_number === undefined ? matchItem.seat_number ?? matchItem.Seat ?? null : seat_number;
          }
        }
        track(itemReceiversRef, key, {
          ...data,
          item_name,
          category,
          course,
          seat_number
        });
        setOrders(prev => prev.map(order => {
          const sameOrder = data?.order_id != null && String(order?.order_id ?? "") === String(data.order_id);
          const sameTicket = data?.ticket_instance_id && normalizeTicket(order?.ticket_instance_id) === normalizeTicket(data.ticket_instance_id);
          if (!sameOrder && !sameTicket) return order;
          return patchItemDisplayBoxes(order, data?.sender_station_id ?? data?.station_id, data?.item_id, {
            received: true
          }, {
            createIfMissing: true
          });
        }));
        scheduleDisplayOnlyRefresh("item-received", data);

        // If Expo sends a course assignment, keep the one from the lowest Expo station ID.
        const senderRole = String(data?.sender_role || "").toLowerCase();
        const expoCourse = Number(data?.course);
        const expoSenderStationId = Number(data?.sender_station_id);
        const incomingIsAddon = isAddonFlag(data?.is_addon) || isAddonFlag(data?.isAddon) || isAddonFlag(data?.isaddon);
        const hasExpoCourse = senderRole === "expo" && Number.isFinite(expoCourse);
        if (senderRole === "expo") {
          logCourseSync("socket-item-received-expo", {
            order_id,
            item_id,
            incomingIsAddon,
            expoCourseRaw: data?.course,
            hasExpoCourse,
            sync_courses_with_expo: !!settingsRef.current?.sync_courses_with_expo
          });
        }
        if (hasExpoCourse) {
          const exactItemId = normalizeId(item_id);
          const normalizedItemId = normalizeItem(item_id);
          const mapKey = `${String(order_id)}::${exactItemId}`;
          const existing = expoCourseByOrderItemRef.current.get(mapKey);
          const incomingSidFinite = Number.isFinite(expoSenderStationId);
          const existingSidFinite = Number.isFinite(existing?.senderStationId);
          const sameSender = incomingSidFinite && existingSidFinite && expoSenderStationId === existing.senderStationId;
          const incomingQuality = (incomingIsAddon ? 2 : 0) + (expoCourse > 0 ? 1 : 0);
          const existingQuality = (existing?.isAddon ? 2 : 0) + (Number(existing?.course) > 0 ? 1 : 0);
          const wouldDowngradeAddonCourse = sameSender && !!existing?.isAddon && Number(existing?.course) > 0 && (!incomingIsAddon || expoCourse <= 0);
          const shouldReplace = !existing || !existingSidFinite || incomingSidFinite && expoSenderStationId < existing.senderStationId ||
          // Same Expo station can correct prior values, but never downgrade addon/high-course data.
          sameSender && !wouldDowngradeAddonCourse && incomingQuality >= existingQuality;
          logCourseSync("expo-course-map-decision", {
            order_id,
            item_id,
            mapKey,
            normalizedItemId,
            incomingIsAddon,
            expoCourse,
            expoSenderStationId,
            existing: existing || null,
            sameSender,
            incomingQuality,
            existingQuality,
            wouldDowngradeAddonCourse,
            shouldReplace
          });
          if (shouldReplace) {
            expoCourseByOrderItemRef.current.set(mapKey, {
              course: expoCourse,
              senderStationId: expoSenderStationId,
              isAddon: incomingIsAddon ? 1 : 0
            });
            // Add-on fallback key for cases where station payload IDs differ by suffix.
            if (incomingIsAddon) {
              expoCourseByOrderItemRef.current.set(`${String(order_id)}::${normalizedItemId}`, {
                course: expoCourse,
                senderStationId: expoSenderStationId,
                isAddonFallback: true
              });
            }

            // If this prep station has sync enabled, patch current hydrated orders immediately.
            if (role !== "expo" && !!settingsRef.current?.sync_courses_with_expo) {
              setOrders(prev => prev.map(o => {
                if (String(o?.order_id ?? "") !== String(order_id)) return o;
                let matchedExact = false;
                let nextItems = (o.items || []).map(it => {
                  if (normalizeId(it?.ID) !== exactItemId) return it;
                  matchedExact = true;
                  return {
                    ...it,
                    course: expoCourse
                  };
                });

                // Add-on fallback: if no exact hit, match add-ons by normalized ID.
                if (!matchedExact && incomingIsAddon) {
                  nextItems = nextItems.map(it => {
                    const isAddonItem = isAddonFlag(it?.is_addon) || isAddonFlag(it?.isAddon) || isAddonFlag(it?.isaddon);
                    if (!isAddonItem) return it;
                    if (normalizeItem(it?.ID) !== normalizedItemId) return it;
                    logCourseSync("prep-live-patch-addon-fallback", {
                      order_id,
                      item_id,
                      itemIdOnTicket: it?.ID,
                      appliedCourse: expoCourse
                    });
                    return {
                      ...it,
                      course: expoCourse
                    };
                  });
                }
                if (matchedExact) {
                  logCourseSync("prep-live-patch-exact", {
                    order_id,
                    item_id,
                    appliedCourse: expoCourse
                  });
                }
                return {
                  ...o,
                  items: nextItems
                };
              }));
            }
          }
        }

        // Prep stations persist a station-scoped addon truth row when Expo marks an item as addon.
        // This prevents addon state from disappearing after refresh/rebuild from DB events.
        if (role !== "expo" && senderRole === "expo" && incomingIsAddon && order_id != null && item_id != null) {
          const ackKey = `${String(order_id)}::${normalizeId(item_id)}`;
          if (!addonSyncAckRef.current.has(ackKey)) {
            addonSyncAckRef.current.add(ackKey);
            const orderForAck = findOrder({
              ticket_instance_id: data?.ticket_instance_id,
              order_id
            });
            const itemForAck = findItemInOrder(orderForAck, item_id);
            socket.emit("item-received", {
              order_id,
              ticket_instance_id: orderForAck?.ticket_instance_id ?? data?.ticket_instance_id ?? null,
              item_id,
              item_name: data?.item_name ?? itemForAck?.Name ?? null,
              category: data?.category ?? itemForAck?.Category ?? null,
              course: Number.isFinite(expoCourse) ? expoCourse : Number.isFinite(Number(itemForAck?.course)) ? Number(itemForAck?.course) : null,
              is_addon: 1,
              seat_number: data?.seat_number ?? (() => {
                const s = deriveSeat(itemForAck);
                return s != null ? String(s) : null;
              })(),
              station_id: stationId,
              user_info: getUserInfoFor(orderForAck) ?? data?.user_info ?? null,
              sender_screen_id: mountIdRef.current,
              sender_role: role,
              sender_station_id: stationId,
              addon_sync_ack: true
            });
          }
        }
      },
      // used to keep track of what stations fired all items on all tickets
      "fire-ticket": (payload = {}) => {
        if (isFromMe(payload)) return;
        const key = payload.ticket_instance_id || payload.order_id;
        const order = findOrder({
          ticket_instance_id: payload.ticket_instance_id,
          order_id: payload.order_id
        });
        const items = (order?.items || []).filter(i => !i.isCourseSeparator && !i.isMessage);
        const enriched = {
          ...payload,
          order_id: payload.order_id ?? order?.order_id ?? null,
          table_name: order?.table_name ?? null,
          destination: order?.destination ?? null,
          server_name: order?.server_name ?? null,
          total_items: items.length,
          item_names_preview: items.map(i => i.Name).filter(Boolean).slice(0, 12),
          kind: "fire-ticket"
        };
        track(fireTicketEmittersRef, key, enriched);
        const shouldApply = shouldApplyRemoteFireEvent(payload);
        if (shouldApply) {
          // Don't re-emit
          FireAllItems(payload, false);
        }
      },
      // used to keep track of what stations fired what courses
      "fire-course": (payload = {}) => {
        if (isFromMe(payload)) return;
        const key = payload.ticket_instance_id || payload.order_id;
        const order = findOrder({
          ticket_instance_id: payload.ticket_instance_id,
          order_id: payload.order_id
        });
        const courseItems = (order?.items || []).filter(i => i.course === payload.course && !i.isCourseSeparator && !i.isMessage);
        const enriched = {
          ...payload,
          order_id: payload.order_id ?? order?.order_id ?? null,
          table_name: order?.table_name ?? null,
          destination: order?.destination ?? null,
          server_name: order?.server_name ?? null,
          course_index: payload.course,
          course_number: Number.isFinite(payload.course) ? payload.course + 1 : null,
          course_item_count: courseItems.length,
          course_item_names: courseItems.map(i => i.Name).filter(Boolean),
          kind: "fire-course"
        };
        track(fireCourseEmittersRef, key, enriched);
        const {
          ticket_instance_id,
          order_id,
          course
        } = payload;
        const reason = String(payload?.reason || "").toLowerCase();
        const shouldApply = shouldApplyRemoteFireEvent(payload) || reason === "fire_next_course_pos" || reason === "fire_course_pos" || reason === "fire_category_pos";
        if (shouldApply) {
          // Don't re-emit
          fireCourse(course, {
            ticket_instance_id,
            order_id
          }, false);
          if (typeof onCourseFired === "function") {
            onCourseFired({
              source: "remote-fire-course",
              station_id: stationId,
              ticket_instance_id,
              order_id,
              course,
              reason: payload?.reason ?? null
            });
          }
          const target = findOrder({
            ticket_instance_id,
            order_id
          });
          const shouldShowCourseMessage = reason !== "fire_category_pos";
          if (shouldShowCourseMessage && target?.ticket_instance_id && Number.isFinite(Number(course))) {
            addMessage(target.ticket_instance_id, `FIRE COURSE ${Number(course) + 1}`);
          }
        }
      },
      "fire-item": (payload = {}) => {
        if (isFromMe(payload)) return;
        const order = findOrder({
          ticket_instance_id: payload.ticket_instance_id,
          order_id: payload.order_id
        });
        const item = findItemInOrder(order, payload.item_id);
        track(fireItemEmittersRef, payload.item_id || `${payload.ticket_instance_id}:${payload.order_id}`, {
          ...payload,
          order_id: payload.order_id ?? order?.order_id ?? null,
          item_name: payload.item_name ?? item?.Name ?? null,
          course: payload.course ?? item?.course ?? null,
          kind: "fire-item"
        });
        if (shouldApplyRemoteFireEvent(payload)) {
          fireItem(payload.item_id, {
            ticket_instance_id: payload.ticket_instance_id,
            order_id: payload.order_id
          }, false);
        }
      },
      // used to keep track of what stations bumped what tickets
      "bump-ticket": (data = {}) => {
        logKdsActivity("socket.bump_ticket_received", data);
        track(bumpTicketEmittersRef, data.ticket_instance_id || data.order_id, {
          ...data,
          kind: "bump-ticket"
        });
        if (data?.ticket_instance_id) {
          bumpedTicketHistoryRef.current.add(normalizeTicket(data.ticket_instance_id));
        }
        if (data?.order_id != null) {
          bumpedOrderHistoryRef.current.add(String(data.order_id));
        }
        if (isDisplayOnlyBumpMode()) {
          setOrders(prev => applyTicketDisplayBumpState(prev, data, true));
        }
        if (shouldApplyMirroredBumpSyncEvent(data)) {
          scheduleRemoteStateRefresh();
        }
      },
      "unbump-ticket": (data = {}) => {
        logKdsActivity("socket.unbump_ticket_received", data);
        const fromMe = isFromMe(data);
        if (fromMe) return;
        if (isDisplayOnlyBumpMode()) {
          setOrders(prev => applyTicketDisplayBumpState(prev, data, false));
        }
        if (shouldApplyMirroredBumpSyncEvent(data)) {
          fetchOrders();
        }
      },
      // used to keep track of what stations bumped what course
      "bump-course": (data = {}) => {
        logKdsActivity("socket.bump_course_received", data);
        const key = `${data.ticket_instance_id}::${data.course}`;
        const order = findOrder({
          ticket_instance_id: data.ticket_instance_id,
          order_id: data.order_id
        });
        const courseItems = (order?.items || []).filter(i => i.course === data.course && !i.isCourseSeparator && !i.isMessage);
        const enriched = {
          ...data,
          order_id: data.order_id ?? order?.order_id ?? null,
          table_name: order?.table_name ?? null,
          destination: order?.destination ?? null,
          server_name: order?.server_name ?? null,
          course_index: data.course,
          course_number: Number.isFinite(data.course) ? data.course + 1 : null,
          course_item_count: courseItems.length,
          course_item_names: courseItems.map(i => i.Name).filter(Boolean),
          kind: "bump-course"
        };
        track(bumpCourseEmittersRef, key, enriched);
        if (isDisplayOnlyBumpMode()) {
          setOrders(prev => applyCourseDisplayBumpState(prev, data, true));
        }
        if (shouldApplyMirroredBumpSyncEvent(data)) {
          scheduleRemoteStateRefresh();
        }
      },
      "unbump-course": (data = {}) => {
        if (isFromMe(data)) return;
        if (isDisplayOnlyBumpMode()) {
          setOrders(prev => applyCourseDisplayBumpState(prev, data, false));
        }
        if (shouldApplyMirroredBumpSyncEvent(data)) {
          scheduleRemoteStateRefresh();
        }
      },
      // used to keep track of what stations bumped what items
      "bump-item": (data = {}) => {
        logKdsActivity("socket.bump_item_received", data);
        const fromMe = isFromMe(data);
        const order = findOrder({
          ticket_instance_id: data.ticket_instance_id,
          order_id: data.order_id
        });
        const item = findItemInOrder(order, data.item_id);
        const enriched = {
          ...data,
          order_id: data.order_id ?? order?.order_id ?? null,
          item_name: data.item_name ?? item?.Name ?? null,
          category: data.category ?? item?.Category ?? null,
          course: data.course ?? item?.course ?? null,
          table_name: order?.table_name ?? null,
          destination: order?.destination ?? null,
          server_name: order?.server_name ?? null,
          kind: "bump-item"
        };
        track(bumpItemEmittersRef, data.item_id, enriched);
        if (isDisplayOnlyBumpMode()) {
          setOrders(prev => applyItemDisplayBumpState(prev, data, data?.isBumped !== false));
        }
        if (shouldApplyMirroredBumpSyncEvent(data) || Number(data?.sender_station_id) === Number(stationId)) {
          if (!fromMe) {
            setOrders(prev => applyRemoteBumpItemState(prev, data));
          }
          scheduleRemoteStateRefresh();
        }
      },
      "unbump-item": (data = {}) => {
        const fromMe = isFromMe(data);
        if (fromMe) return;
        if (isDisplayOnlyBumpMode()) {
          setOrders(prev => applyItemDisplayBumpState(prev, data, false));
        }
        if (shouldApplyMirroredBumpSyncEvent(data)) {
          if (!fromMe) {
            setOrders(prev => applyRemoteBumpItemState(prev, {
              ...data,
              isBumped: false,
              bumpedAt: null
            }));
          }
          scheduleRemoteStateRefresh();
        }
      },
      // used to keep track of what stations void what items
      "void-item": (data = {}) => {
        if (isFromMe(data)) return;
        if (typeof onVoidedItem === "function") {
          onVoidedItem({
            source: "remote-socket",
            station_id: data?.sender_station_id ?? data?.station_id ?? null,
            order_id: data?.order_id ?? null,
            ticket_instance_id: data?.ticket_instance_id ?? null,
            item_id: data?.item_id ?? null
          });
        }
        const order = findOrder({
          ticket_instance_id: data.ticket_instance_id,
          order_id: data.order_id
        });
        const item = findItemInOrder(order, data.item_id);
        const enriched = {
          ...data,
          order_id: data.order_id ?? order?.order_id ?? null,
          item_name: data.item_name ?? item?.Name ?? null,
          category: data.category ?? item?.Category ?? null,
          course: data.course ?? item?.course ?? null,
          table_name: order?.table_name ?? null,
          destination: order?.destination ?? null,
          server_name: order?.server_name ?? null,
          kind: "void-item"
        };
        track(voidItemEmittersRef, data.item_id, enriched);
        // APPLY void to UI state so item immediately shows as voided
        const tNeedle = normalizeTicket(data.ticket_instance_id);
        const iNeedle = normalizeItem(data.item_id);
        setOrders(prev => {
          // 1) Mark the item voided across all cards/pages for this ticket
          const next = prev.map(t => {
            if (normalizeTicket(t.ticket_instance_id) !== tNeedle) return t;
            const items = (t.items || []).map(it => {
              if (normalizeItem(it.ID) !== iNeedle) return it;
              return {
                ...it,
                isVoided: true,
                status: "voided",
                // Preserve incoming bump state for full-ticket voids so cards stay visible as voided.
                isBumped: typeof data?.isBumped === "boolean" ? data.isBumped : it.isBumped,
                bumpedAt: data?.bumpedAt ?? (typeof data?.isBumped === "boolean" ? data.isBumped ? it.bumpedAt ?? new Date().toISOString() : null : it.bumpedAt),
                voidReason: data.voidReason ?? it.voidReason ?? null
              };
            });
            const msgs = new Set(t.order_messages || []);
            msgs.add("Some items were voided.");
            return {
              ...t,
              items,
              order_messages: Array.from(msgs)
            };
          });
          return next;
        });
      },
      "void-ticket": (data = {}) => {
        if (isFromMe(data)) return;
        const tNeedle = normalizeTicket(data.ticket_instance_id);
        const oNeedle = data.order_id != null ? String(data.order_id) : null;
        setOrders(prev => prev.map(t => {
          const sameTicket = !!tNeedle && normalizeTicket(t.ticket_instance_id) === tNeedle;
          const sameOrder = oNeedle != null && String(t.order_id ?? "") === String(oNeedle);
          if (!sameTicket && !sameOrder) return t;
          const items = (t.items || []).map(it => {
            if (it?.isCourseSeparator || it?.isMessage || it?.isFireItem) return it;
            return {
              ...it,
              isVoided: true,
              status: "voided",
              isBumped: true,
              bumpedAt: it.bumpedAt ?? new Date().toISOString(),
              voidReason: data.voidReason ?? it.voidReason ?? null
            };
          });
          const msgs = new Set(t.order_messages || []);
          msgs.add("This ticket has been voided.");
          return {
            ...t,
            items,
            order_messages: Array.from(msgs)
          };
        }));
      }
    };

    // Attach: clear any stale listeners first, then bind ours
    Object.entries(handlers).forEach(([evt, fn]) => {
      socket.off(evt, fn);
      socket.on(evt, fn);
    });
    // 1-second UI tick
    const tick = setInterval(() => forceUpdate(x => x + 1), 1000);

    // Cleanup
    return () => {
      clearInterval(tick);
      if (displayOnlyRefreshTimerRef.current) {
        clearTimeout(displayOnlyRefreshTimerRef.current);
        displayOnlyRefreshTimerRef.current = null;
      }
      Object.entries(handlers).forEach(([evt, fn]) => socket.off(evt, fn));
    };
  }, [stationId, role]);
  function moveTicketByNewList(newOrderList) {
    setOrders(newOrderList);
  }

  //Takes in a ticket and course number and fires all items in it.
  function fireCourse(courseNumber, arg = null, shouldEmit = true, emitExtras = null) {
    const normalize = id => String(id).split("-")[0];
    let ticketId = null;
    let orderId = null;
    if (typeof arg === "string") {
      ticketId = arg;
    } else if (arg && typeof arg === "object") {
      ticketId = arg.ticket_instance_id ?? arg.ticketId ?? null;
      orderId = arg.order_id ?? arg.orderId ?? null;
    }

    // optional guard: never allow totally global
    if (!ticketId && !orderId) {
      console.warn("[fireCourse] called without ticket/order; ignoring");
      return;
    }
    const now = new Date().toISOString();
    const sourceOrder = findOrder({
      ticket_instance_id: ticketId,
      order_id: orderId
    });
    const promotedOrder = buildProjectedFireCourseOrder(sourceOrder, courseNumber);
    setOrders(prev => prev.map(order => {
      const byTicket = !!ticketId && normalize(order.ticket_instance_id) === normalize(ticketId);
      const byOrder = orderId != null && String(order.order_id) === String(orderId);

      // Cross-station tickets can have different ticket_instance_id values.
      // If order_id is present, allow it to match so fire-course propagates in realtime.
      let matches = false;
      if (ticketId && orderId != null) {
        matches = byTicket || byOrder;
      } else if (ticketId) {
        matches = byTicket;
      } else if (orderId != null) {
        matches = byOrder;
      }
      if (!matches) return order;
      const items = order.items.map(item => {
        if (item.course === courseNumber && !item.isFired && !item.isBumped && !item.isCourseSeparator && !item.isMessage && !item.isFireItem) {
          return {
            ...item,
            isFired: true,
            firedAt: now
          };
        }
        return item;
      });
      return {
        ...order,
        items
      };
    }));
    requestTicketFirePromotion(promotedOrder);
    requestUnparkTicketWhenItemsAreFired(promotedOrder);

    //  emit part of your existing code stays the same
    if (shouldEmit && (ticketId || orderId)) {
      const o = findOrder({
        ticket_instance_id: ticketId,
        order_id: orderId
      });
      const courseItems = (o?.items || []).filter(i => i.course === courseNumber && !i.isCourseSeparator && !i.isMessage);
      const courseItemIds = courseItems.map(i => i.ID).filter(id => id != null && String(id).trim() !== "");
      socket.emit("fire-course", {
        ...(ticketId ? {
          ticket_instance_id: ticketId
        } : {}),
        ...(orderId ? {
          order_id: orderId
        } : {}),
        course: courseNumber,
        course_item_names: courseItems.map(i => i.Name).filter(Boolean),
        course_item_count: courseItems.length,
        item_ids: courseItemIds,
        user_info: getUserInfoFor(o),
        ...(emitExtras && typeof emitExtras === "object" ? emitExtras : {}),
        sender_screen_id: mountIdRef.current,
        sender_role: role,
        sender_station_id: stationId,
        sentAt: new Date().toISOString()
      });
    }
  }
  function FireAllItems(arg = null, shouldEmit = true) {
    // Accept both FireAllItems(ticketId) and FireAllItems({ ticket_instance_id, order_id })
    let ticketId = null;
    let orderId = null;
    if (typeof arg === "string") {
      ticketId = arg;
    } else if (arg && typeof arg === "object") {
      ticketId = arg.ticket_instance_id ?? arg.ticketId ?? null;
      orderId = arg.order_id ?? arg.orderId ?? null;
    }
    const now = new Date().toISOString();
    const normalizeTicketId = id => String(id).split("-")[0];
    const sourceOrder = findOrder({
      ticket_instance_id: ticketId,
      order_id: orderId
    });
    const promotedOrder = buildProjectedFireAllItemsOrder(sourceOrder);
    setOrders(prevOrders => prevOrders.map(order => {
      const ticketMatch = ticketId == null || normalizeTicketId(order.ticket_instance_id) === normalizeTicketId(ticketId);
      const orderMatch = orderId == null || String(order.order_id) === String(orderId);
      if (!ticketMatch && !orderMatch) return order;
      const updatedItems = order.items.map(item => {
        if (!item.isFired && !item.isBumped && !item.isCourseSeparator && !item.isMessage && !item.isFireItem) {
          return {
            ...item,
            isFired: true,
            firedAt: now
          };
        }
        return item;
      });
      return {
        ...order,
        items: updatedItems
      };
    }));
    requestTicketFirePromotion(promotedOrder);
    requestUnparkTicketWhenItemsAreFired(promotedOrder);
    if (shouldEmit && (ticketId || orderId)) {
      // Try to include both IDs in the payload to help other stations match
      if (!orderId && ticketId) {
        const match = ordersRef.current.find(o => normalizeTicketId(o.ticket_instance_id) === normalizeTicketId(ticketId));
        if (match?.order_id != null) orderId = match.order_id;
      }
      const o = findOrder({
        ticket_instance_id: ticketId,
        order_id: orderId
      });
      const fireableItems = (o?.items || []).filter(i => !i.isCourseSeparator && !i.isMessage && !i.isFireItem && !i.isFired && !i.isBumped);
      const totalItems = fireableItems.length;
      const fireItemIds = fireableItems.map(i => i.ID).filter(id => id != null && String(id).trim() !== "");
      socket.emit("fire-ticket", {
        ...(ticketId ? {
          ticket_instance_id: ticketId
        } : {}),
        ...(orderId ? {
          order_id: orderId
        } : {}),
        total_items: totalItems,
        item_ids: fireItemIds,
        user_info: getUserInfoFor(o),
        sender_screen_id: mountIdRef.current,
        sender_role: role,
        sender_station_id: stationId,
        sentAt: new Date().toISOString()
      });
    }
  }
  function fireItem(itemId, arg = null, shouldEmit = true) {
    if (!itemId) return;
    let ticketId = null;
    let orderId = null;
    if (typeof arg === "string") {
      ticketId = arg;
    } else if (arg && typeof arg === "object") {
      ticketId = arg.ticket_instance_id ?? arg.ticketId ?? null;
      orderId = arg.order_id ?? arg.orderId ?? null;
    }
    const normalize = id => String(id ?? "").split("-")[0];
    const now = new Date().toISOString();
    const sourceOrder = findOrder({
      ticket_instance_id: ticketId,
      order_id: orderId
    });
    const promotedOrder = buildProjectedFireItemOrder(sourceOrder, itemId);
    setOrders(prev => prev.map(order => {
      const byTicket = !!ticketId && normalize(order.ticket_instance_id) === normalize(ticketId);
      const byOrder = orderId != null && String(order.order_id) === String(orderId);
      let matches = false;
      if (ticketId && orderId != null) {
        matches = byTicket || byOrder;
      } else if (ticketId) {
        matches = byTicket;
      } else if (orderId != null) {
        matches = byOrder;
      }
      if (!matches) return order;
      const items = order.items.map(item => {
        if (String(item.ID) === String(itemId) && !item.isFired && !item.isBumped && !item.isCourseSeparator && !item.isMessage && !item.isFireItem) {
          return {
            ...item,
            isFired: true,
            firedAt: now
          };
        }
        return item;
      });
      return {
        ...order,
        items
      };
    }));
    requestTicketFirePromotion(promotedOrder);
    requestUnparkTicketWhenItemsAreFired(promotedOrder);
    if (shouldEmit) {
      const o = findOrder({
        ticket_instance_id: ticketId,
        order_id: orderId
      });
      const item = (o?.items || []).find(entry => String(entry.ID) === String(itemId));
      if (!item) return;
      socket.emit("fire-item", {
        ...(ticketId ? {
          ticket_instance_id: ticketId
        } : {}),
        ...(orderId != null ? {
          order_id: orderId
        } : {}),
        item_id: item.ID,
        item_name: item.Name,
        course: item.course ?? null,
        user_info: getUserInfoFor(o),
        sender_screen_id: mountIdRef.current,
        sender_role: role,
        sender_station_id: stationId,
        sentAt: new Date().toISOString()
      });
    }
  }

  // Add a display message to every card/page of a given ticket
  function addMessage(ticketId, text) {
    const line = String(text || "").trim();
    if (!line) return;
    setOrders(prev => prev.map(t => {
      if (normalizeTicket(t.ticket_instance_id) !== normalizeTicket(ticketId)) return t;
      const next = new Set(t.order_messages || []);
      next.add(line);
      return {
        ...t,
        order_messages: [...next]
      };
    }));
  }

  // Fetches all orders from the db
  async function fetchOrders() {
    try {
      const beforeRefresh = summarizeOrders(ordersRef.current);
      logKdsActivity("kds.refresh_started", { before: beforeRefresh });
      // 1. Fetch raw order rows + bumped history + category metadata
      const [res, bumpedRes, resCat] = await Promise.all([
        apiFetch(`/orders?station_id=${stationId}`),
        apiFetch(`/orders/bumped-today?station_id=${stationId}`),
        apiFetch(`/orders/catlist/sorted`)
      ]);
      const rawData = await res.json();
      const bumpedData = await bumpedRes.json();
      const categoryMeta = await resCat.json();
      const bumpedTicketHistory = new Set(
        (Array.isArray(bumpedData) ? bumpedData : [])
          .map(row => normalizeTicket(row?.ticket_instance_id))
          .filter(Boolean)
      );
      const bumpedOrderHistory = new Set(
        (Array.isArray(bumpedData) ? bumpedData : [])
          .map(row => row?.order_id != null ? String(row.order_id) : "")
          .filter(Boolean)
      );
      bumpedTicketHistoryRef.current = bumpedTicketHistory;
      bumpedOrderHistoryRef.current = bumpedOrderHistory;
      const validCategories = categoryMeta.filter(cat => cat && typeof cat.name === "string");
      const categoryMap = new Map(validCategories.map(cat => [normalizeCategoryKey(cat.name), cat]));
      categoryMapRef.current = categoryMap;

      // 3. Group rows by ticket_instance_id
      const groupedMap = new Map();
      const isRawUtilityLine = item => {
        const name = String(item?.Name || "").trim();
        const category = String(item?.Category || "").trim();
        const isSeparator = /^\*{3,}.*\*{3,}$/.test(name) || category === "Course Separator";
        const isMessage = name === "* MESSAGE *";
        const isFireCategoryControl = !!extractFireLabel(category);
        const isFireLine = name === "------ FIRE ------" || /FIRE/i.test(name) && name !== "* MESSAGE *" || typeof item?.ID === "string" && item.ID.includes("FIRE");
        return isSeparator || isMessage || isFireLine || isFireCategoryControl;
      };
      const isVoidedItem = item => Number(item?.TransType) === 2 || String(item?.status || "").toLowerCase() === "voided";
      const isAddonItem = item => item?.is_addon === 1 || item?.is_addon === "1" || item?.is_addon === true || item?.isAddon === 1 || item?.isAddon === "1" || item?.isAddon === true;
      const toTime = val => {
        const t = Date.parse(String(val || ""));
        return Number.isFinite(t) ? t : 0;
      };
      const shouldTreatFetchedOrderAsReopened = groupedOrder => {
        if (settingsRef.current?.merge_mode !== "merge") return false;
        const normalizedTicketId = normalizeTicket(groupedOrder?.ticket_instance_id);
        const orderIdKey = groupedOrder?.order_id != null ? String(groupedOrder.order_id) : "";
        const wasBumped =
          (!!normalizedTicketId && bumpedTicketHistory.has(normalizedTicketId)) ||
          (!!orderIdKey && bumpedOrderHistory.has(orderIdKey));
        const hasAddonItems = (groupedOrder?.Item || []).some(isAddonItem);
        const isModifiedOrder = String(groupedOrder?.order_type || groupedOrder?.OrderType || "").toUpperCase() === "MODIFIED";
        return wasBumped && hasAddonItems && isModifiedOrder;
      };
      const shouldDisplayFetchedAddonAsNormalReopen = groupedOrder => {
        const existingItems = Array.isArray(groupedOrder?.existingItems) ? groupedOrder.existingItems : [];
        const visibleRealItems = existingItems.filter(item => !item?.isCourseSeparator && !item?.isMessage && !item?.isFireItem);
        return visibleRealItems.length === 0;
      };
      for (const row of rawData) {
        const ticketId = row.ticket_instance_id;
        if (!ticketId) continue;
        const parsed = safeJsonParse(row.ticket_json);
        const rawItems = parsed?.Item;
        const userInfo = parsed?.UserInfo || null;
        const parsedOrderMessages = parsed?.OrderMessages || parsed?.orderMessages || null;
        const parsedOrderMessageLines = parsedOrderMessages ? Object.entries(parsedOrderMessages).filter(([key]) => String(key).startsWith("S")).map(([, value]) => String(value).trim()).filter(Boolean) : [];
        const rowStartAt = row.first_received_at || row.received_at;
        const reopenBaseAt = row.received_at || row.first_received_at || rowStartAt;
        const items = Array.isArray(rawItems) ? rawItems : rawItems ? [rawItems] : [];
        const existingOrder = ordersRef.current.find(o => {
          const sameTicket = normalizeTicket(o?.ticket_instance_id) === normalizeTicket(ticketId);
          const sameOrder = row?.order_id != null && String(o?.order_id ?? "") === String(row.order_id);
          return sameTicket || (settingsRef.current?.merge_mode === "merge" && sameOrder);
        }) || null;
        const existingOrderMessages = Array.isArray(existingOrder?.order_messages) ? existingOrder.order_messages : [];
        const existingTaggedFields = {
          covers: existingOrder?.covers ?? null,
          allergies: existingOrder?.allergies ?? null,
          notes: existingOrder?.notes ?? null
        };
        if (!groupedMap.has(ticketId)) {
          groupedMap.set(ticketId, {
            ticket_instance_id: ticketId,
            ID: row.order_id,
            order_id: row.order_id,
            table_name: row.table_name,
            server_name: row.server_name,
            station_id: row.station_id,
            status: row.status,
            parkedAt: normalizeStoredUtcDateTime(row.parked_at),
            unparkTime: normalizeStoredUtcDateTime(row.unpark_time),
            createdAt: rowStartAt,
            latestReceivedAt: reopenBaseAt,
            destination: row.destination,
            order_type: row.order_type ?? parsed?.OrderType ?? null,
            OrderType: parsed?.OrderType ?? row.order_type ?? null,
            user_info: userInfo,
            OrderMessages: parsedOrderMessages,
            orderMessages: parsedOrderMessages,
            preservedOrderMessages: [...parsedOrderMessageLines, ...existingOrderMessages],
            ...existingTaggedFields,
            isRush: false,
            ticket_bump_display_boxes: Array.isArray(row.ticket_bump_display_boxes) ? row.ticket_bump_display_boxes : [],
            course_bump_display_boxes: row?.course_bump_display_boxes && typeof row.course_bump_display_boxes === "object" ? row.course_bump_display_boxes : {},
            existingItems: Array.isArray(existingOrder?.items) ? existingOrder.items.map(item => ({
              ...item
            })) : [],
            Item: []
          });
        } else {
          const existingStart = groupedMap.get(ticketId).createdAt;
          const minStart = toTime(rowStartAt) && toTime(existingStart) ? toTime(rowStartAt) < toTime(existingStart) ? rowStartAt : existingStart : existingStart || rowStartAt;
          groupedMap.get(ticketId).createdAt = minStart;
          const existingLatest = groupedMap.get(ticketId).latestReceivedAt;
          const nextLatest = toTime(reopenBaseAt) >= toTime(existingLatest) ? reopenBaseAt : existingLatest;
          groupedMap.get(ticketId).latestReceivedAt = nextLatest || reopenBaseAt;
          if (parsedOrderMessages) {
            groupedMap.get(ticketId).OrderMessages = parsedOrderMessages;
            groupedMap.get(ticketId).orderMessages = parsedOrderMessages;
          }
          if (!groupedMap.get(ticketId).order_type && (row.order_type || parsed?.OrderType)) {
            groupedMap.get(ticketId).order_type = row.order_type ?? parsed?.OrderType ?? null;
            groupedMap.get(ticketId).OrderType = parsed?.OrderType ?? row.order_type ?? null;
          }
          const mergedPreservedMessages = [...parsedOrderMessageLines, ...(groupedMap.get(ticketId).preservedOrderMessages || []), ...existingOrderMessages];
          groupedMap.get(ticketId).preservedOrderMessages = [...new Set(mergedPreservedMessages)];
          for (const [key, value] of Object.entries(existingTaggedFields)) {
            if (groupedMap.get(ticketId)[key] == null && value != null && value !== "") {
              groupedMap.get(ticketId)[key] = value;
            }
          }
          if (Array.isArray(row.ticket_bump_display_boxes) && row.ticket_bump_display_boxes.length) {
            groupedMap.get(ticketId).ticket_bump_display_boxes = row.ticket_bump_display_boxes;
          }
          if (row?.course_bump_display_boxes && typeof row.course_bump_display_boxes === "object") {
            groupedMap.get(ticketId).course_bump_display_boxes = row.course_bump_display_boxes;
          }
          if ((!Array.isArray(groupedMap.get(ticketId).existingItems) || groupedMap.get(ticketId).existingItems.length === 0) && Array.isArray(existingOrder?.items) && existingOrder.items.length > 0) {
            groupedMap.get(ticketId).existingItems = existingOrder.items.map(item => ({
              ...item
            }));
          }
        }
        for (const item of items) {
          const name = (item?.Name || "").trim();
          const category = (item?.Category || "").trim();
          const isCourseSeparator = /^\*{3,}.*\*{3,}$/.test(name) || category === "Course Separator";
          if (!isCourseSeparator && parseInt(item.KDSStation) !== stationId) continue;
          const existingLocalItem = existingOrder?.items?.find(existingItem => normalizeItem(existingItem?.ID) === normalizeItem(item?.ID)) || null;
          const existingExactItem = existingOrder?.items?.find(existingItem => normalizeId(existingItem?.ID) === normalizeId(item?.ID)) || null;
          const existingBaseMatches = (existingOrder?.items || []).filter(existingItem => normalizeItem(existingItem?.ID) === normalizeItem(item?.ID));
          const incomingAddon = isAddonItem(item);
          const matchedLocalItem = existingExactItem || existingLocalItem;
          const matchedLocalStatus = String(matchedLocalItem?.status || "").toLowerCase();
          const matchedLocalBumped =
            !!matchedLocalItem?.isBumped || matchedLocalStatus === "bumped";
          const matchedLocalVoided =
            !!matchedLocalItem?.isVoided || matchedLocalStatus === "voided" || Number(matchedLocalItem?.TransType) === 2;
          const preserveLocalBumpState = matchedLocalBumped && !matchedLocalVoided && !isVoidedItem(item);
          const preserveAddonDisplay = incomingAddon ? false : !!matchedLocalItem?.suppressAddonDisplay;
          const preserveLocalNonAddonState = incomingAddon && matchedLocalItem && !isAddonItem(matchedLocalItem);
          const itemCreatedAt =
            item.createdAt ||
            existingExactItem?.createdAt ||
            (incomingAddon ? row.received_at || reopenBaseAt || rowStartAt : rowStartAt);
          if (!isCourseSeparator && (isAddonItem(item) || existingBaseMatches.length > 1 || existingLocalItem && !existingExactItem)) {
            logAddonDupCourse("fetch-row-existing-match", {
              ticket_instance_id: ticketId,
              order_id: row?.order_id ?? null,
              incoming: getAddonCourseDebugSnapshot(item),
              rowStartAt,
              rowReceivedAt: row?.received_at ?? null,
              existingLocalMatchType: existingExactItem ? "exact" : existingLocalItem ? "base-id" : "none",
              existingLocalItem: existingLocalItem ? getAddonCourseDebugSnapshot(existingLocalItem) : null,
              existingBaseMatches: existingBaseMatches.map(getAddonCourseDebugSnapshot),
              preserveLocalBumpState,
              preserveAddonDisplay,
              preserveLocalNonAddonState
            });
          }
          groupedMap.get(ticketId).Item.push({
            ...item,
            is_addon: preserveLocalNonAddonState ? 0 : item?.is_addon,
            isAddon: preserveLocalNonAddonState ? false : item?.isAddon,
            isaddon: preserveLocalNonAddonState ? 0 : item?.isaddon,
            status: preserveLocalBumpState ? "bumped" : (item.status ?? row.status),
            isBumped: preserveLocalBumpState ? true : !!item?.isBumped,
            bumpedAt: preserveLocalBumpState
              ? (matchedLocalItem?.bumpedAt ?? matchedLocalItem?.bumped_at ?? item?.bumpedAt ?? item?.bumped_at ?? null)
              : (item?.bumpedAt ?? item?.bumped_at ?? null),
            createdAt: preserveAddonDisplay ? matchedLocalItem?.createdAt || reopenBaseAt : itemCreatedAt,
            suppressAddonDisplay: preserveAddonDisplay,
            showsAddon: preserveLocalNonAddonState ? false : (incomingAddon ? !preserveAddonDisplay : (preserveAddonDisplay ? false : item?.showsAddon))
          });
        }
      }

      // 4. Dedupe repeated item snapshots while keeping utility lines untouched.
      // If duplicate rows exist for the same item, prefer the voided state; otherwise prefer newest.
      for (const grouped of groupedMap.values()) {
        const deduped = [];
        const seenByItemKey = new Map();
        for (const item of grouped.Item || []) {
          if (isRawUtilityLine(item)) {
            deduped.push(item);
            continue;
          }
          const itemKey = normalizeItem(item?.ID);
          if (!itemKey) {
            deduped.push(item);
            continue;
          }
          const existingIndex = seenByItemKey.get(itemKey);
          if (existingIndex == null) {
            seenByItemKey.set(itemKey, deduped.length);
            deduped.push(item);
            continue;
          }
          const existing = deduped[existingIndex];
          const existingVoided = isVoidedItem(existing);
          const incomingVoided = isVoidedItem(item);
          const existingAddon = isAddonItem(existing);
          const incomingAddon = isAddonItem(item);

          // Keep addon truth sticky across duplicate snapshots:
          // if we already have addon=true for this item, do not replace with addon=false.
          const wouldDowngradeAddon = existingAddon && !incomingAddon;
          const shouldReplace = !existingAddon && incomingAddon || !existingVoided && incomingVoided || !wouldDowngradeAddon && existingVoided === incomingVoided && toTime(item?.createdAt) >= toTime(existing?.createdAt);
          logAddonDupCourse("fetch-dedupe-same-base-id", {
            ticket_instance_id: grouped?.ticket_instance_id ?? null,
            order_id: grouped?.order_id ?? null,
            itemKey,
            existing: getAddonCourseDebugSnapshot(existing),
            incoming: getAddonCourseDebugSnapshot(item),
            existingAddon,
            incomingAddon,
            shouldReplace
          });
          if (shouldReplace) {
            deduped[existingIndex] = item;
          }
        }
        grouped.Item = deduped;
        const reopenFetchedDisplay = shouldTreatFetchedOrderAsReopened(grouped);
        if (reopenFetchedDisplay) {
          const reopenAt = grouped?.latestReceivedAt || grouped?.createdAt || new Date().toISOString();
          const displayAddonAsNormalReopen = shouldDisplayFetchedAddonAsNormalReopen(grouped);
          grouped.createdAt = reopenAt;
          grouped.status = "pending";
          grouped.Item = (grouped.Item || []).map(item => {
            if (isRawUtilityLine(item)) return item;
            const isAddon = isAddonItem(item);
            const statusLower = String(item?.status || "").toLowerCase();
            const preserveBumpState =
              !isVoidedItem(item) &&
              (!!item?.isBumped || statusLower === "bumped");
            return {
              ...item,
              is_addon: displayAddonAsNormalReopen && isAddon ? 0 : item?.is_addon,
              isAddon: displayAddonAsNormalReopen && isAddon ? false : item?.isAddon,
              isaddon: displayAddonAsNormalReopen && isAddon ? 0 : item?.isaddon,
              createdAt: reopenAt,
              suppressAddonDisplay: displayAddonAsNormalReopen && isAddon ? false : !!item?.suppressAddonDisplay,
              showsAddon: displayAddonAsNormalReopen ? false : (isAddon ? !item?.suppressAddonDisplay : false),
              isBumped: isVoidedItem(item) ? !!item?.isBumped : preserveBumpState,
              bumpedAt: isVoidedItem(item) || preserveBumpState
                ? item?.bumpedAt ?? item?.bumped_at ?? null
                : null,
              status: isVoidedItem(item)
                ? "voided"
                : preserveBumpState
                  ? "bumped"
                  : "active"
            };
          });
        }
      }

      // 5. Convert to array and filter out tickets that have no meaningful items.
      // Keep voided items displayable so full-ticket voids remain visible after refresh.
      const groupedOrders = Array.from(groupedMap.values()).filter(order => {
        if (!order.Item.length) return false;
        const allNonDisplay = order.Item.every(item => {
          const name = (item?.Name || "").trim();
          const category = (item?.Category || "").trim();
          const isSeparator = /^\*{3,}.*\*{3,}$/.test(name) || category === "Course Separator";
          const isMessage = name === "* MESSAGE *";
          const isFireLine = name === "------ FIRE ------" || /FIRE/i.test(name) && name !== "* MESSAGE *" || typeof item?.ID === "string" && item.ID.includes("FIRE");
          return isSeparator || isMessage || isFireLine;
        });
        return !allNonDisplay;
      });

      // 6. Hydrate
      const hydratedOrders = groupedOrders.flatMap(order => hydrateOrder(order, categoryMapRef.current, settingsRef.current, Array.isArray(order?.existingItems) ? order.existingItems : [], null, Array.isArray(order?.preservedOrderMessages) ? order.preservedOrderMessages : [], getHandledSetForTicket(order.ticket_instance_id)));
      const afterRefresh = summarizeOrders(hydratedOrders);
      const unexpectedlyUnbumped = beforeRefresh.flatMap(before => {
        const after = afterRefresh.find(candidate => candidate.ticket_instance_id === before.ticket_instance_id);
        if (!after) return [];
        return before.bumped_items.filter(itemId => after.active_items.includes(itemId)).map(item_id => ({ ticket_instance_id: before.ticket_instance_id, item_id }));
      });
      logKdsActivity(unexpectedlyUnbumped.length ? "kds.refresh_unexpected_unbump" : "kds.refresh_completed", {
        before: beforeRefresh, after: afterRefresh, unexpectedly_unbumped: unexpectedlyUnbumped,
      });
      setOrders(hydratedOrders);
    } catch (err) {
      console.error(" EXPO Failed to fetch orders:", err);
    }
  }

  // Helper for parsing condiments JSON
  function safeJsonParse(str) {
    try {
      return JSON.parse(str);
    } catch {
      return [];
    }
  }
  function extractTaggedOrderMessages(messages = []) {
    const tags = {};
    const filteredMessages = [];

    // Define which tags you want to extract (case-insensitive)
    const tagMatchers = [{
      key: "covers",
      regex: /^covers:\s*.+$/i // matches "Covers: 5" or "Covers: five", case-insensitive
    }
    // Add more matchers here later if needed
    ];
    for (const rawMsg of messages) {
      const message = String(rawMsg).trim();

      // Skip Count-type system messages
      if (/^count:\s*\d+$/i.test(message)) continue;
      let matched = false;
      for (const {
        key,
        regex
      } of tagMatchers) {
        if (regex.test(message)) {
          tags[key] = message; // Store full original message (e.g. "Covers: 9")
          matched = true;
          break;
        }
      }
      if (!matched) {
        filteredMessages.push(message); // Not a recognized tag
      }
    }
    return {
      tags,
      filteredMessages
    };
  }
  const FIRE_LABEL_RE = /^\s*[-–—]\s*FIRE\s+(.+?)\s*[-–—]\s*$/i;
  function extractFireLabel(cat) {
    const m = String(cat || "").match(FIRE_LABEL_RE);
    return m ? m[1].trim() : null;
  }
  const norm = s => String(s || "").trim();
  function handleEncoreFireCourse(order) {
    const labels = new Set((Array.isArray(order.Item) ? order.Item : [order.Item]).filter(Boolean).map(it => extractFireLabel(it?.Category)).filter(Boolean).map(norm));
    const controlIds = new Set((Array.isArray(order.Item) ? order.Item : [order.Item]).filter(Boolean).map(it => normalizeId(it?.ID)).filter(Boolean));
    if (!labels.size) return;
    const orderId = order?.ID ?? order?.order_id ?? null;
    if (orderId == null) return;
    const matchingTickets = (ordersRef.current || []).filter(t => String(t?.order_id) === String(orderId));
    if (!matchingTickets.length) {
      return;
    }
    for (const ticket of matchingTickets) {
      const dedupeSet = getHandledSetForTicket(ticket?.ticket_instance_id || orderId);
      for (const label of labels) {
        const matchingItems = (ticket?.items || []).filter(i => {
          if (i?.isCourseSeparator || i?.isMessage || i?.isFireItem || i?.isBumped || i?.isFired) {
            return false;
          }
          return norm(i?.Category).toUpperCase() === norm(label).toUpperCase();
        });
        if (!matchingItems.length) continue;
        const courses = Array.from(new Set(matchingItems.map(i => Number(i?.course)).filter(n => Number.isFinite(n)))).sort((a, b) => a - b);
        for (const courseNumber of courses) {
          const controlKeyPart = Array.from(controlIds).join("|") || "fire-control";
          const dedupeKey = `fire-category-pos:${controlKeyPart}:${norm(label).toUpperCase()}:${courseNumber}`;
          if (dedupeSet.has(dedupeKey)) continue;
          dedupeSet.add(dedupeKey);
          fireCourse(courseNumber, {
            ticket_instance_id: ticket?.ticket_instance_id,
            order_id: ticket?.order_id
          }, true, {
            reason: "fire_category_pos",
            source: "pos_fire_category",
            fire_label: label
          });
          if (typeof onCourseFired === "function") {
            onCourseFired({
              source: "encore-fire-category",
              station_id: stationId,
              ticket_instance_id: ticket?.ticket_instance_id ?? null,
              order_id: ticket?.order_id ?? null,
              course: courseNumber,
              reason: "fire_category_pos",
              fire_label: label
            });
          }
          if (ticket?.ticket_instance_id) {
            addMessage(ticket.ticket_instance_id, `FIRE ${label}`);
          }
        }
        if (courses.length === 0) {
          const controlKeyPart = Array.from(controlIds).join("|") || "fire-control";
          for (const item of matchingItems) {
            const itemId = normalizeId(item?.ID);
            if (!itemId) continue;
            const dedupeKey = `fire-category-pos-item:${controlKeyPart}:${norm(label).toUpperCase()}:${itemId}`;
            if (dedupeSet.has(dedupeKey)) continue;
            dedupeSet.add(dedupeKey);
            fireItem(itemId, {
              ticket_instance_id: ticket?.ticket_instance_id,
              order_id: ticket?.order_id
            }, true);
          }
          if (ticket?.ticket_instance_id) {
            addMessage(ticket.ticket_instance_id, `FIRE ${label}`);
          }
        }
      }
    }
  }
  function markSpecialFlags(item) {
    const name = String(item?.Name || "").trim();
    const category = String(item?.Category || "").trim();
    const isSeparator = /^\*{3,}.*\*{3,}$/.test(name) || category === "Course Separator";
    const isMessage = name === "* MESSAGE *";
    const isFireCategoryControl = !!extractFireLabel(category);
    const isFireLine = name === "------ FIRE ------" || name.toUpperCase().includes("FIRE") && name !== "* MESSAGE *" || typeof item.ID === "string" && item.ID.includes("FIRE");
    const dbCourse = Number(item?.course);
    return {
      ...item,
      isCourseSeparator: !!isSeparator,
      isMessage: !!isMessage,
      isFireItem: !!(isFireLine || isFireCategoryControl),
      _dbCourse: Number.isFinite(dbCourse) ? dbCourse : null
    };
  }
  function deriveSeat(it) {
    // direct fields first
    const direct = it?.seat_number ?? it?.Seat ?? it?.SeatNumber ?? it?.seat ?? it?.PreModifier?.S0 ?? it?.PreModifier?.S1;
    const fromDirect = String(direct ?? "").trim();
    if (fromDirect) {
      const m = fromDirect.match(/(\d+)/);
      if (m) return m[1];
    }

    // PreModifier scan
    const pm = it?.PreModifier || {};
    for (const k of ["S0", "S1", "Seat", "SeatNumber"]) {
      const v = String(pm?.[k] ?? "").trim();
      if (!v) continue;
      const m = v.match(/(\d+)/);
      if (m) return m[1];
    }

    // Condiments like "SEAT 4"
    const conds = Array.isArray(it?.Condiment) ? it.Condiment : it?.Condiment ? [it.Condiment] : [];
    for (const c of conds) {
      const m = String(c?.Name || "").match(/SEAT\s*#?\s*(\d+)/i);
      if (m) return m[1];
    }
    return null;
  }

  // Processes a new order received over the socket connection.
  const handleNewOrder = order => {
    const currentSettings = settingsRef.current;
    const mergeMode = currentSettings.merge_mode;
    const ticketId = order.ticket_instance_id;
    const isRush = !!order.isRush || !!order?.header?.isRush;
    const isFired = !!order.isFired || !!order?.header?.isFired;
    const stationIdMatch = order.station_id === stationId;
    const shouldUseOrderMessages = stationIdMatch || isRush;
    const isModified = String(order?.OrderType || "").toUpperCase() === "MODIFIED";
    const isFireOrderType = String(order?.OrderType || "").toUpperCase() === "FIRE";
    const isEncoreTT = Number(order?.TransType ?? order?.transtype) === 3;
    const isEncoreFullVoid = currentSettings.is_encore && Number(order?.TransType ?? order?.transtype) === 2;
    const arrItems = (Array.isArray(order.Item) ? order.Item : [order.Item]).filter(Boolean);

    // Handle full-ticket void envelopes immediately (before station gating).
    if (Number(order?.TransType ?? order?.transtype) === 2) {
      const fullVoidOrderId = order?.ID ?? order?.order_id ?? null;
      const fullVoidTicketRoot = normalizeTicket(ticketId ?? order?.ticket_instance_id ?? null);
      setOrders(prev => prev.map(t => {
        const byOrderId = fullVoidOrderId != null && String(t.order_id ?? "") === String(fullVoidOrderId);
        const byTicketRoot = !!fullVoidTicketRoot && normalizeTicket(t.ticket_instance_id) === fullVoidTicketRoot;
        const isMatch = byOrderId || byTicketRoot;
        if (!isMatch) return t;
        emitVoidTicket(t.ticket_instance_id, "FULL VOID");
        const voidedItems = (t.items || []).map(item => {
          const updated = {
            ...item,
            isVoided: true,
            status: "voided",
            isBumped: true,
            bumpedAt: item.bumpedAt ?? new Date().toISOString()
          };
          emitVoidItem(t.ticket_instance_id, updated);
          return updated;
        });
        const messages = new Set(t.order_messages || []);
        messages.add("This ticket has been voided.");
        return {
          ...t,
          items: voidedItems,
          order_messages: Array.from(messages)
        };
      }));
      return;
    }
    const normalizeItemId = id => String(id || "").split("-")[0];
    const isUtilityLine = it => {
      const name = String(it?.Name || "").trim();
      const cat = String(it?.Category || "").trim();
      const isSeparator = /^\*{3,}.*\*{3,}$/.test(name) || cat === "Course Separator";
      const isMessage = name === "* MESSAGE *";
      const isFireCategoryControl = !!extractFireLabel(cat);
      const isFireLine = name === "------ FIRE ------" || /FIRE/i.test(name) && name !== "* MESSAGE *" || typeof it?.ID === "string" && it.ID.includes("FIRE");
      return isSeparator || isMessage || isFireLine || isFireCategoryControl;
    };

    // Items for THIS station (as sent by backend)
    const relevantForThisStation = arrItems.filter(it => parseInt(it?.KDSStation) === stationId);
    const fireNextCourseMessageItems = relevantForThisStation.filter(it => {
      const isMsg = String(it?.Name || "").trim() === "* MESSAGE *";
      if (!isMsg) return false;
      const condiments = Array.isArray(it?.Condiment) ? it.Condiment : [];
      return condiments.some(c => String(c?.Name || "").trim().toUpperCase().startsWith("FIRE NEXT COURSE"));
    });
    const fireSpecificCourseDirectives = relevantForThisStation.flatMap(it => {
      const isMsg = String(it?.Name || "").trim() === "* MESSAGE *";
      if (!isMsg) return [];
      const condiments = Array.isArray(it?.Condiment) ? it.Condiment : [];
      return condiments.map(c => String(c?.Name || "").trim().toUpperCase()).map(line => {
        const m = line.match(/^FIRE COURSE\s+(\d+)\b/);
        if (!m) return null;
        const oneBased = Number(m[1]);
        if (!Number.isFinite(oneBased) || oneBased <= 0) return null;
        return {
          itemId: normalizeId(it?.ID),
          courseOneBased: oneBased,
          courseZeroBased: oneBased - 1
        };
      }).filter(Boolean);
    });
    const firedCategoriesFromEnvelope = new Set(relevantForThisStation.flatMap(it => {
      const out = [];
      const fromCategory = extractFireLabel(it?.Category);
      if (fromCategory) out.push(fromCategory);
      const condiments = Array.isArray(it?.Condiment) ? it.Condiment : [];
      for (const c of condiments) {
        const m = String(c?.Name || "").match(/^--(.+?)--$/);
        if (m?.[1]) out.push(m[1].trim());
      }
      return out;
    }).filter(Boolean));

    // Explicit void IDs for THIS station (non-Encore explicit void, and also used for void-only update marking)
    const voidedIdsForThisStation = new Set(relevantForThisStation.filter(it => Number(it?.TransType) === 2 || String(it?.status || "").toLowerCase() === "voided").map(it => normalizeItemId(it?.ID)).filter(Boolean));

    // ENCORE: "missing item means void" should only consider non-void incoming real items.
    // Explicit void envelopes (TransType=2 rows) are handled separately below.
    const incomingRealIdsForThisStation = new Set(relevantForThisStation.filter(it => !isUtilityLine(it)).filter(it => !(Number(it?.TransType) === 2 || String(it?.status || "").toLowerCase() === "voided")).map(it => normalizeItemId(it?.ID)).filter(Boolean));
    const hasExplicitVoidRows = relevantForThisStation.some(it => Number(it?.TransType) === 2 || String(it?.status || "").toLowerCase() === "voided");
    const existingTicketForEncore = findOrder({
      ticket_instance_id: ticketId,
      order_id: order?.ID ?? order?.order_id
    });
    const existingRealIdsForThisStation = new Set((existingTicketForEncore?.items || []).filter(it => parseInt(it?.KDSStation) === stationId).filter(it => !isUtilityLine(it)).map(it => normalizeItemId(it?.ID)).filter(Boolean));
    const incomingIntroducesNewRealItems = incomingRealIdsForThisStation.size > 0 && Array.from(incomingRealIdsForThisStation).some(iid => !existingRealIdsForThisStation.has(iid));
    const shouldApplyEncoreMissingVoids = currentSettings.is_encore && !incomingIntroducesNewRealItems && !hasExplicitVoidRows;
    const isVoidOnlyForThisStation = relevantForThisStation.length > 0 && relevantForThisStation.every(it => {
      // ignore non-display utility lines when deciding "void-only"
      if (isUtilityLine(it)) return true;
      const t = Number(it?.TransType);
      const statusLower = String(it?.status || "").toLowerCase();
      return t === 2 || statusLower === "voided";
    });

    // If this payload is void-only AND we no longer have the ticket in local state,
    // ignore it completely (prevents resurrecting after bump).
    if (isVoidOnlyForThisStation) {
      const existing = findOrder({
        ticket_instance_id: ticketId,
        order_id: order?.ID ?? order?.order_id
      });
      if (!existing) {
        return;
      }
    }
    const hasFireMarker = arrItems.some(it => extractFireLabel(it?.Category) !== null);
    const looksLikeEncoreFire = isEncoreTT && (isFireOrderType || hasFireMarker);
    const isFireOrder = looksLikeEncoreFire;

    // helper: "real" kitchen line (not sep/message/fire) for THIS station
    const isRealKitchenItemForThisStation = it => {
      const isVoided = Number(it?.TransType) === 2 || String(it?.status || "").toLowerCase() === "voided";
      return parseInt(it?.KDSStation) === stationId && !isUtilityLine(it) && !isVoided;
    };
    const hasRealKitchenItems = arrItems.some(isRealKitchenItemForThisStation);

    // Allow FIRE to bypass station check
    if (!stationIdMatch && !isRush && !(currentSettings.is_encore && (looksLikeEncoreFire || isEncoreFullVoid))) {
      return;
    }

    // Apply Encore missing-item voids to an item list (void always wins)
    const applyEncoreMissingVoids = (itemsList, nowIso) => {
      if (!currentSettings.is_encore) return itemsList;
      if (!incomingRealIdsForThisStation || incomingRealIdsForThisStation.size === 0) return itemsList;
      return (itemsList || []).map(item => {
        // Only apply to this station's items
        if (parseInt(item?.KDSStation) !== stationId) return item;

        // Do not touch utility lines
        if (isUtilityLine(item)) return item;

        // Void always wins (don't unvoid)
        if (item.isVoided) return item;
        if (String(item?.status || "").toLowerCase() === "voided") return {
          ...item,
          isVoided: true
        };
        const norm = normalizeItemId(item?.ID);
        if (!incomingRealIdsForThisStation.has(norm)) {
          if (typeof onVoidedItem === "function") {
            onVoidedItem({
              source: "encore-missing-item-void",
              station_id: stationId,
              order_id: order?.ID ?? order?.order_id ?? null,
              ticket_instance_id: ticketId ?? null,
              item_id: item?.ID ?? null
            });
          }
          return {
            ...item,
            isVoided: true,
            isBumped: true,
            bumpedAt: item.bumpedAt || nowIso
          };
        }
        return item;
      });
    };

    // Full-ticket void envelope (TransType=2, often with no item lines):
    // apply immediately so live UI matches DB/reports.
    if (Number(order.TransType ?? order.transtype) === 2) {
      const fullVoidOrderId = order?.ID ?? order?.order_id ?? null;
      const fullVoidTicketRoot = normalizeTicket(ticketId ?? order?.ticket_instance_id ?? null);
      setOrders(prev => prev.map(t => {
        const byOrderId = fullVoidOrderId != null && String(t.order_id ?? "") === String(fullVoidOrderId);
        const byTicketRoot = !!fullVoidTicketRoot && normalizeTicket(t.ticket_instance_id) === fullVoidTicketRoot;
        const isMatch = byOrderId || byTicketRoot;
        if (!isMatch) return t;
        emitVoidTicket(t.ticket_instance_id, "FULL VOID");
        const voidedItems = (t.items || []).map(item => {
          const updated = {
            ...item,
            isVoided: true,
            status: "voided",
            isBumped: true,
            bumpedAt: item.bumpedAt ?? new Date().toISOString()
          };
          emitVoidItem(t.ticket_instance_id, updated);
          return updated;
        });
        const messages = new Set(t.order_messages || []);
        messages.add("This ticket has been voided.");
        return {
          ...t,
          items: voidedItems,
          order_messages: Array.from(messages)
        };
      }));
      return;
    }

    // ENCORE: proactively mark missing items as voided on the existing visible ticket,
    // even if this update has no real kitchen items (because voids come in as "missing").
    if (currentSettings.is_encore) {
      if (shouldApplyEncoreMissingVoids) {
        const nowIso = new Date().toISOString();
        setOrders(prev => prev.map(t => {
          if (normalizeTicket(t.ticket_instance_id) !== normalizeTicket(ticketId)) return t;
          const updatedItems = applyEncoreMissingVoids(t.items, nowIso);
          return updatedItems === t.items ? t : {
            ...t,
            items: updatedItems
          };
        }));
      }

      // FULL VOID (Encore-style order void)
      if (Number(order.TransType ?? order.transtype) === 2) {
        const fullVoidOrderId = order?.ID ?? order?.order_id ?? null;
        const fullVoidTicketRoot = normalizeTicket(ticketId ?? order?.ticket_instance_id ?? null);
        setOrders(prev => prev.map(t => {
          const byOrderId = fullVoidOrderId != null && String(t.order_id ?? "") === String(fullVoidOrderId);
          const byTicketRoot = !!fullVoidTicketRoot && normalizeTicket(t.ticket_instance_id) === fullVoidTicketRoot;
          const isMatch = byOrderId || byTicketRoot;
          if (!isMatch) return t;
          emitVoidTicket(t.ticket_instance_id, "FULL VOID");
          const voidedItems = (t.items || []).map(item => {
            const updated = {
              ...item,
              isVoided: true,
              status: "voided",
              isBumped: true,
              bumpedAt: item.bumpedAt ?? new Date().toISOString()
            };
            emitVoidItem(t.ticket_instance_id, updated);
            return updated;
          });
          const messages = new Set(t.order_messages || []);
          messages.add("This ticket has been voided.");
          return {
            ...t,
            items: voidedItems,
            order_messages: Array.from(messages)
          };
        }));
        return;
      }

      // MODIFIED (explicit void detect) - still supported if it happens
      if (isModified) {
        const incomingItems = Array.isArray(order.Item) ? order.Item : [order.Item];
        const modifiedOrderId = order?.ID ?? order?.order_id ?? null;
        const explicitlyVoidedIDs = new Set(incomingItems.filter(item => Number(item?.TransType) === 2).map(item => normalizeId(item.ID)));
        let didVoidAny = false;
        setOrders(prev => prev.map(ticket => {
          if (modifiedOrderId == null) return ticket;
          if (String(ticket.order_id) !== String(modifiedOrderId)) return ticket;
          const updatedItems = (ticket.items || []).map(item => {
            const id = normalizeId(item.ID);
            if (String(item.ID || "").startsWith("sep-auto")) return item;
            if (explicitlyVoidedIDs.has(id)) {
              didVoidAny = true;
              const updated = {
                ...item,
                isVoided: true,
                isBumped: true
              };
              emitVoidItem(ticket.ticket_instance_id, updated);
              return updated;
            }
            return item;
          });
          const messages = new Set(ticket.order_messages || []);
          if (didVoidAny) messages.add("Some items were voided.");
          return {
            ...ticket,
            items: updatedItems,
            order_messages: Array.from(messages)
          };
        }));
        // continue to merge logic if there are add-ons
      }

      // ENCORE FIRE (course bump)
      if (looksLikeEncoreFire && !isModified) {
        handleEncoreFireCourse(order);
        if (!hasRealKitchenItems) {
          return; // FIRE-only envelope: avoid clobbering headers
        }
        // else continue to merge/add-ons
      }
    } else {}

    // Strict gating: if this station has no real displayable items, stop here (no emit, no card)
    // BUT: if the payload contains explicit voided item IDs for a ticket we already have visible,
    // mark those existing items as voided so the UI shows "Voided" instead of ignoring the update.
    if (!hasRealKitchenItems) {
      const incomingOrderMessages = order.OrderMessages || order.orderMessages || {};
      const incomingMessageLines = extractTicketMessages(incomingOrderMessages);
      if (incomingMessageLines.length > 0) {
        const {
          tags,
          filteredMessages
        } = extractTaggedOrderMessages(incomingMessageLines);
        setOrders(prev => prev.map(t => {
          const sameTicket = normalizeTicket(t.ticket_instance_id) === normalizeTicket(ticketId);
          const sameOrder = String(t.order_id ?? "") === String(order?.ID ?? order?.order_id ?? "");
          if (!sameTicket && !sameOrder) return t;

          const mergedMessages = [...new Set([...(t.order_messages || []), ...filteredMessages.map(msg => String(msg).trim()).filter(Boolean)])];
          return {
            ...t,
            order_messages: mergedMessages,
            ...Object.fromEntries(Object.entries(tags).filter(([, value]) => value != null && value !== "")),
          };
        }));
      }
      if (fireSpecificCourseDirectives.length > 0) {
        const dedupeSet = getHandledSetForTicket(ticketId || order?.ID || order?.order_id);
        const dedupeKeys = fireSpecificCourseDirectives.map(d => `fire-course:${d.itemId}:${d.courseZeroBased}`);
        const hasNewMessage = dedupeKeys.some(k => !dedupeSet.has(k));
        if (hasNewMessage) {
          dedupeKeys.forEach(k => dedupeSet.add(k));
          const existingOrder = findOrder({
            ticket_instance_id: ticketId,
            order_id: order?.ID ?? order?.order_id
          });
          const targetTicketId = existingOrder?.ticket_instance_id ?? ticketId ?? null;
          const targetOrderId = existingOrder?.order_id ?? order?.ID ?? order?.order_id ?? null;
          for (const directive of fireSpecificCourseDirectives) {
            const requestedCourse = Number(directive.courseZeroBased);
            if (!Number.isFinite(requestedCourse)) continue;
            const eligibleInRequestedCourse = (existingOrder?.items || []).filter(item => {
              if (item?.isCourseSeparator || item?.isMessage || item?.isFireItem || item?.isVoided || item?.isBumped || item?.isFired) {
                return false;
              }
              return Number(item?.course) === requestedCourse;
            });
            if (eligibleInRequestedCourse.length === 0) continue;
            fireCourse(requestedCourse, {
              ticket_instance_id: targetTicketId,
              order_id: targetOrderId
            }, true, {
              reason: "fire_course_pos",
              source: "pos_message",
              requested_course: requestedCourse
            });
            if (typeof onCourseFired === "function") {
              onCourseFired({
                source: "encore-fire-course-directive",
                station_id: stationId,
                ticket_instance_id: targetTicketId,
                order_id: targetOrderId,
                course: requestedCourse,
                reason: "fire_course_pos"
              });
            }
            if (targetTicketId) {
              addMessage(targetTicketId, `FIRE COURSE ${requestedCourse + 1}`);
            }
          }
        }
      }
      if (fireNextCourseMessageItems.length > 0) {
        const dedupeSet = getHandledSetForTicket(ticketId || order?.ID || order?.order_id);
        const dedupeKeys = fireNextCourseMessageItems.map(it => `fire-next-course:${normalizeId(it?.ID)}`);
        const hasNewMessage = dedupeKeys.some(k => !dedupeSet.has(k));
        if (hasNewMessage) {
          dedupeKeys.forEach(k => dedupeSet.add(k));
          const existingOrder = findOrder({
            ticket_instance_id: ticketId,
            order_id: order?.ID ?? order?.order_id
          });
          const targetTicketId = existingOrder?.ticket_instance_id ?? ticketId ?? null;
          const targetOrderId = existingOrder?.order_id ?? order?.ID ?? order?.order_id ?? null;
          const eligibleItems = (existingOrder?.items || []).filter(item => {
            if (item?.isCourseSeparator || item?.isMessage || item?.isFireItem || item?.isVoided || item?.isBumped || item?.isFired) {
              return false;
            }
            return Number.isFinite(Number(item?.course));
          });
          const nextCourse = eligibleItems.length ? Math.min(...eligibleItems.map(i => Number(i.course))) : null;
          if (Number.isFinite(nextCourse)) {
            fireCourse(nextCourse, {
              ticket_instance_id: targetTicketId,
              order_id: targetOrderId
            }, true, {
              reason: "fire_next_course_pos",
              source: "pos_message"
            });
            if (typeof onCourseFired === "function") {
              onCourseFired({
                source: "encore-fire-next-course",
                station_id: stationId,
                ticket_instance_id: targetTicketId,
                order_id: targetOrderId,
                course: nextCourse,
                reason: "fire_next_course_pos"
              });
            }
            if (targetTicketId) addMessage(targetTicketId, `FIRE COURSE ${Number(nextCourse) + 1}`);
          }
        }
      }
      if (isFireOrderType) {
        const nowIso = new Date().toISOString();
        const fireMsgs = firedCategoriesFromEnvelope.size > 0 ? Array.from(firedCategoriesFromEnvelope).map(c => `FIRE ${c}`) : ["FIRE"];
        setOrders(prev => prev.map(t => {
          const sameTicket = normalizeTicket(t.ticket_instance_id) === normalizeTicket(ticketId);
          const sameOrder = String(t.order_id ?? "") === String(order?.ID ?? order?.order_id ?? "");
          if (!sameTicket && !sameOrder) return t;
          const nextItems = (t.items || []).map(item => {
            if (item?.isCourseSeparator || item?.isMessage || item?.isFireItem || item?.isVoided || item?.isBumped) {
              return item;
            }
            const itemCategory = String(item?.Category || "").trim();
            const shouldFire = firedCategoriesFromEnvelope.size === 0 || firedCategoriesFromEnvelope.has(itemCategory);
            if (!shouldFire) return item;
            return {
              ...item,
              isFired: true,
              firedAt: item.firedAt || nowIso
            };
          });
          const mergedMessages = new Set(t.order_messages || []);
          fireMsgs.forEach(m => mergedMessages.add(m));
          return {
            ...t,
            items: nextItems,
            order_messages: Array.from(mergedMessages)
          };
        }));
      }
      if (voidedIdsForThisStation.size > 0) {
        const nowIso = new Date().toISOString();
        setOrders(prev => prev.map(t => {
          if (normalizeTicket(t.ticket_instance_id) !== normalizeTicket(ticketId)) return t;
          const updatedItems = (t.items || []).map(item => {
            const norm = normalizeItemId(item?.ID);
            if (!voidedIdsForThisStation.has(norm)) return item;
            if (typeof onVoidedItem === "function" && !item?.isVoided) {
              onVoidedItem({
                source: "encore-explicit-item-void",
                station_id: stationId,
                order_id: order?.ID ?? order?.order_id ?? null,
                ticket_instance_id: ticketId ?? null,
                item_id: item?.ID ?? null
              });
            }
            return {
              ...item,
              isVoided: true,
              isBumped: true,
              bumpedAt: item.bumpedAt || nowIso
            };
          });
          return {
            ...t,
            items: updatedItems
          };
        }));
      }
      return;
    }
    const newItems = Array.isArray(order.Item) ? order.Item : [order.Item];

    // Only items for this station
    const relevantItems = newItems.filter(i => parseInt(i.KDSStation) === stationId);
    const isOnlySeparators = relevantItems.length > 0 && relevantItems.every(i => {
      const name = (i?.Name || "").trim();
      const category = (i?.Category || "").trim();
      return /^\*{3,}.*\*{3,}$/.test(name) || category === "Course Separator";
    });
    if (isOnlySeparators) {
      return;
    }

    // ---------- Enriched, echo-safe, de-duped 'order-received' emit ----------
    const fromOtherScreen = order?.sender_screen_id && order.sender_screen_id !== mountIdRef.current;
    const msgObj = order.OrderMessages || order.orderMessages || {};
    const ticket_messages = extractTicketMessages(msgObj);
    const coversFromMsgs = extractCoversFromMessagesList(ticket_messages);
    const table_name = order.table_name ?? order.GuestTable ?? null;
    const server_name = order.server_name ?? order.ServerName ?? null;
    const covers = order.covers ?? coversFromMsgs ?? null;
    if (shouldEmitOrderReceived(order) && !fromOtherScreen) {
      socket.emit("order-received", {
        order_id: order.order_id ?? order.ID,
        ticket_instance_id: order.ticket_instance_id,
        sender_screen_id: mountIdRef.current,
        sender_role: role,
        sender_station_id: stationId,
        received_at: new Date().toISOString(),
        user_info: getUserInfoFor(order),
        table_name,
        server_name,
        covers,
        ticket_messages
      });
      const k = orderKey(order);
      if (k) lastOrderReceivedRef.current.set(k, Date.now());
    }
    // ------------------------------------------------------------------------

    // === compute course for each relevant item BEFORE emitting ===
    // IMPORTANT: for separator-based coursing we must use the FULL item array (arrItems),
    // not just relevantItems, because separators often don't have a KDSStation.
    const currentCoursing = settingsRef.current;
    let allForCoursing = (Array.isArray(arrItems) ? arrItems : []).map(i => ({
      ...i
    })).map(markSpecialFlags);
    if (currentCoursing.coursing) {
      if (currentCoursing.use_course_separator) {
        allForCoursing = applyCourseSeparatorCoursing(allForCoursing);
      } else {
        allForCoursing = applyCategoryBasedCoursing(allForCoursing, categoryMapRef.current);
      }
    }
    const courseById = new Map(allForCoursing.filter(i => !i.isCourseSeparator && !i.isMessage && !i.isFireItem).map(i => [normalizeId(i.ID), i.course]));
    const localCourseById = new Map(courseById);

    // === EMIT 'item-received' for each REAL item this station should see ===
    const senderMeta = {
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId
    };
    const orderIdForEmit = order.ID ?? order.order_id ?? null;
    logAddonDupCourse("live-course-map-after-separator", {
      ticket_instance_id: ticketId,
      order_id: orderIdForEmit,
      coursing: !!currentCoursing.coursing,
      use_course_separator: !!currentCoursing.use_course_separator,
      items: allForCoursing.map(getAddonCourseDebugSnapshot)
    });
    const normalizedTicketId = normalizeTicket(ticketId);
    const existingVisibleItemsForTicket = ordersRef.current.filter(candidate => normalizedTicketId && normalizeTicket(candidate?.ticket_instance_id) === normalizedTicketId).flatMap(candidate => Array.isArray(candidate?.items) ? candidate.items : []);
    const existingVisibleItemIds = new Set(existingVisibleItemsForTicket.map(item => normalizeId(item?.ID)).filter(Boolean));
    const incomingRealItemsForStation = relevantItems.filter(isRealKitchenItemForThisStation);
    const existingRealCountForStation = existingVisibleItemsForTicket.filter(x => parseInt(x?.KDSStation) === stationId).filter(x => !x?.isCourseSeparator && !x?.isMessage && !x?.isFireItem).length;
    const stationAlreadyHasVisibleTicket = existingRealCountForStation > 0;
    const isModifiedEnvelope = String(order?.OrderType || "").toUpperCase() === "MODIFIED";
    const incomingRealCountForStation = incomingRealItemsForStation.length;
    const addonCandidateByItemId = new Map();
    const effectiveAddonByItemId = new Map();

    incomingRealItemsForStation.forEach(it => {
      const rawIsAddon = isAddonFlag(it?.is_addon) || isAddonFlag(it?.isAddon) || isAddonFlag(it?.isaddon);
      const existedOnCard = existingVisibleItemIds.has(normalizeId(it?.ID));
      const itemTransType = Number(it?.TransType);
      const looksLikeModifiedAddon =
        isModifiedEnvelope &&
        stationAlreadyHasVisibleTicket &&
        !existedOnCard &&
        (
          (Number.isFinite(itemTransType) && itemTransType === 1) ||
          (existingRealCountForStation > 0 &&
            incomingRealCountForStation > 0 &&
            incomingRealCountForStation < existingRealCountForStation)
        );
      const addonCandidate = rawIsAddon || looksLikeModifiedAddon;
      addonCandidateByItemId.set(normalizeId(it?.ID), addonCandidate);
      effectiveAddonByItemId.set(normalizeId(it?.ID), addonCandidate);
    });

    const wasPreviouslyBumpedForDisplay = wasTicketPreviouslyBumpedForDisplay(ticketId, existingVisibleItemsForTicket, orderIdForEmit);
    const reopenTicketDisplayFromBumped = isModifiedEnvelope && wasPreviouslyBumpedForDisplay && incomingRealItemsForStation.some(it => {
      const normId = normalizeId(it?.ID);
      return !!addonCandidateByItemId.get(normId) && !existingVisibleItemIds.has(normId);
    });
    const displayReopenedItemsAsNormal = reopenTicketDisplayFromBumped && !stationAlreadyHasVisibleTicket;
    if (displayReopenedItemsAsNormal) {
      incomingRealItemsForStation.forEach(it => {
        const normId = normalizeId(it?.ID);
        if (!existingVisibleItemIds.has(normId) && addonCandidateByItemId.get(normId)) {
          effectiveAddonByItemId.set(normId, false);
        }
      });
    }
    const reopenDisplayAt = reopenTicketDisplayFromBumped ? new Date().toISOString() : null;
    incomingRealItemsForStation.forEach(it => {
      const normId = normalizeId(it.ID);
      const computedCourse = courseById.get(normId);
      const effectiveIsAddon = !!effectiveAddonByItemId.get(normId);
      let effectiveCourse = computedCourse != null ? Number(computedCourse) : null;
      logAddonDupCourse("live-before-emit-course", {
        ticket_instance_id: ticketId,
        order_id: orderIdForEmit,
        item: getAddonCourseDebugSnapshot(it),
        computedCourse,
        effectiveCourse,
        effectiveIsAddon,
        existingVisibleItemIds: Array.from(existingVisibleItemIds),
        existingMatchingBaseItems: existingVisibleItemsForTicket
          .filter(existing => normalizeItem(existing?.ID) === normalizeItem(it?.ID))
          .map(getAddonCourseDebugSnapshot)
      });
      if (role === "expo" && effectiveIsAddon && (!Number.isFinite(effectiveCourse) || effectiveCourse === 0)) {
        const lastCourse = getLastRealCourseFromCurrentOrder(orderIdForEmit, ticketId);
        if (Number.isFinite(lastCourse)) {
          logCourseSync("expo-emit-addon-course-fallback", {
            order_id: orderIdForEmit,
            ticket_instance_id: ticketId,
            item_id: it.ID,
            computedCourse,
            fallbackCourse: lastCourse,
            rawIsAddon: isAddonFlag(it?.is_addon) || isAddonFlag(it?.isAddon) || isAddonFlag(it?.isaddon),
            looksLikeModifiedAddon: effectiveIsAddon && !(isAddonFlag(it?.is_addon) || isAddonFlag(it?.isAddon) || isAddonFlag(it?.isaddon))
          });
          effectiveCourse = Number(lastCourse);
        }
      }
      logAddonDupCourse("live-final-emit-course", {
        ticket_instance_id: ticketId,
        order_id: orderIdForEmit,
        item_id: it?.ID ?? null,
        item_name: it?.Name ?? null,
        effectiveIsAddon,
        computedCourse,
        emittedCourse: Number.isFinite(effectiveCourse) ? effectiveCourse : null,
        createdAt: it?.createdAt ?? null,
        preparationTime: it?.preparationTime ?? it?.PreparationTime ?? it?.preparation_time ?? it?.prepTime ?? null
      });
      if (role === "expo" && effectiveIsAddon) {
        logCourseSync("expo-emit-addon", {
          order_id: orderIdForEmit,
          ticket_instance_id: ticketId,
          item_id: it.ID,
          computedCourse,
          effectiveCourse,
          rawIsAddon: isAddonFlag(it?.is_addon) || isAddonFlag(it?.isAddon) || isAddonFlag(it?.isaddon),
          looksLikeModifiedAddon: effectiveIsAddon && !(isAddonFlag(it?.is_addon) || isAddonFlag(it?.isAddon) || isAddonFlag(it?.isaddon))
        });
      }
      if (Number.isFinite(effectiveCourse)) {
        localCourseById.set(normId, Number(effectiveCourse));
      }
      socket.emit("item-received", {
        order_id: orderIdForEmit,
        ticket_instance_id: ticketId,
        item_id: it.ID,
        item_name: it.Name,
        category: it.Category,
        course: Number.isFinite(effectiveCourse) ? effectiveCourse : null,
        is_addon: effectiveIsAddon ? 1 : 0,
        seat_number: (() => {
          const s = deriveSeat(it);
          return s != null ? String(s) : null;
        })(),
        station_id: stationId,
        user_info: getUserInfoFor(order),
        ...senderMeta
      });
    });
    const newItemsWithComputedCourses = newItems.map(item => {
      const itemId = normalizeId(item?.ID);
      const effectiveLocalCourse = localCourseById.get(itemId);
      if (!Number.isFinite(Number(effectiveLocalCourse))) return item;
      return {
        ...item,
        course: Number(effectiveLocalCourse)
      };
    });

    // === setOrders logic (merge/new) ===
    setOrders(prev => {
      const ticketExists = prev.some(p => p.ticket_instance_id === ticketId);
      const previousMessages = prev.filter(p => p.ticket_instance_id === ticketId).flatMap(p => p.order_messages || []);
      if (mergeMode === "merge" && ticketExists) {
        const existingItems = prev.filter(p => p.ticket_instance_id === ticketId).flatMap(p => p.items);
        const existingItemIds = new Set(existingItems.map(item => normalizeId(item?.ID)).filter(Boolean));
        const resetAddonDisplayIds = new Set(
          incomingRealItemsForStation
            .filter(item => {
              const normalizedId = normalizeId(item?.ID);
              const isNewDisplayLine = !existingItemIds.has(normalizedId);
              return (reopenTicketDisplayFromBumped && isNewDisplayLine) || (!!effectiveAddonByItemId.get(normalizedId) && isNewDisplayLine);
            })
            .map(item => normalizeId(item?.ID))
            .filter(Boolean)
        );
        // Merge preserves existing items; adds new ones; does not delete missing
        let mergedItems = mergeItems(existingItems, newItemsWithComputedCourses, {
          reopenTicketDisplayFromBumped,
          reopenAt: reopenDisplayAt,
          suppressAddonDisplayItemIds: resetAddonDisplayIds,
          effectiveCourseByItemId: localCourseById
        });

        // ENCORE: after merge, mark missing items as voided (so merge doesn't "unvoid")
        if (shouldApplyEncoreMissingVoids) {
          mergedItems = applyEncoreMissingVoids(mergedItems, new Date().toISOString());
        }
        const preservedCreatedAt = existingItems.reduce((earliest, item) => {
          const itemCreated = new Date(item.createdAt);
          return itemCreated < earliest ? itemCreated : earliest;
        }, new Date());
        const displayCreatedAt = reopenTicketDisplayFromBumped ? reopenDisplayAt : preservedCreatedAt;
        const mergedOrder = {
          ...order,
          status: reopenTicketDisplayFromBumped ? "pending" : order.status,
          createdAt: displayCreatedAt,
          Item: mergedItems.map(item => ({
            ...item,
            createdAt: item.createdAt || displayCreatedAt,
            isBumped: reopenTicketDisplayFromBumped ? !!item.isBumped : item.isBumped || false,
            bumpedAt: reopenTicketDisplayFromBumped ? item.bumpedAt ?? null : item.bumpedAt || null,
            // keep void flag if set
            isVoided: !!item.isVoided
          })),
          isRush,
          isFired
        };
        const incomingMessages = order.OrderMessages || order.orderMessages || {};
        const extractedMessages = Object.entries(incomingMessages).filter(([key]) => key.startsWith("S")).map(([, value]) => String(value).trim());
        const mergedMessages = shouldUseOrderMessages ? [...extractedMessages, ...previousMessages] : previousMessages;
        const uniquePreviousMessages = [...new Set(mergedMessages)];
        const {
          tags: taggedInfo,
          filteredMessages
        } = extractTaggedOrderMessages(uniquePreviousMessages);
        Object.assign(order, taggedInfo);
        const alreadyHandledMessages = getHandledSetForTicket(ticketId);
        const updatedCards = hydrateOrder(mergedOrder, categoryMapRef.current, currentSettings, existingItems, displayCreatedAt, filteredMessages, alreadyHandledMessages);
        const preserveDefined = (oldObj, newObj) => {
          const out = {
            ...oldObj
          };
          for (const [k, v] of Object.entries(newObj || {})) {
            if (v !== undefined && v !== null && v !== "") out[k] = v;
          }
          return out;
        };
        return prev.map(p => {
          if (p.ticket_instance_id !== ticketId) return p;
          const replacement = updatedCards.find(c => c.card === p.card) || updatedCards[0];
          return preserveDefined(p, replacement);
        });
      }
      if (ticketExists && mergeMode !== "merge") {
        return prev;
      }
      const incomingMessages = order.OrderMessages || order.orderMessages || {};
      const extractedMessages = Object.entries(incomingMessages).filter(([key]) => key.startsWith("S")).map(([, value]) => String(value).trim());
      const mergedMessages = shouldUseOrderMessages ? [...extractedMessages, ...previousMessages] : previousMessages;
      const uniquePreviousMessages = [...new Set(mergedMessages)];
      const {
        tags: taggedInfo,
        filteredMessages
      } = extractTaggedOrderMessages(uniquePreviousMessages);
      Object.assign(order, taggedInfo);
      const alreadyHandledMessages = getHandledSetForTicket(ticketId);
      const displayCreatedAt = reopenTicketDisplayFromBumped ? reopenDisplayAt : order.createdAt || new Date();
      // For a brand-new ticket, we cannot infer missing-item voids (there is no prior snapshot),
      // but explicit voids (TransType=2 per item) can still be reflected via downstream logic.
      const hydrated = hydrateOrder({
        ...order,
        status: reopenTicketDisplayFromBumped ? "pending" : order.status,
        createdAt: displayCreatedAt,
        Item: newItemsWithComputedCourses.map(item => {
          const effectiveIsAddon = !!effectiveAddonByItemId.get(normalizeId(item?.ID));
          const normalizedItem = {
            ...item,
            is_addon: effectiveIsAddon ? 1 : 0,
            isAddon: effectiveIsAddon,
            isaddon: effectiveIsAddon ? 1 : 0,
          };
          const nextItem = reopenTicketDisplayFromBumped
            ? reopenTicketItemForDisplay(normalizedItem, displayCreatedAt, {
                suppressAddonDisplay: true,
                forceNonAddon: displayReopenedItemsAsNormal
              })
            : {
                ...normalizedItem,
                createdAt: normalizedItem.createdAt || displayCreatedAt,
                isBumped: false,
                bumpedAt: null,
                isVoided: Number(normalizedItem?.TransType) === 2 || String(normalizedItem?.status || "").toLowerCase() === "voided"
              };
          return nextItem;
        }),
        isRush,
        isFired
      }, categoryMapRef.current, currentSettings, [], displayCreatedAt, filteredMessages, alreadyHandledMessages);
      const updated = insertTicketByPriority(prev, hydrated, isRush, isFired, ticketId, mergeMode);
      return updated;
    });
  };

  // Replaces special character codes (like &amp;) with the actual characters (like &)
  function decodeHtml(html) {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  }

  // Merges incoming items with existing ticket items in 'merge' mode to preserve state and avoid duplicates.
  function mergeItems(existingItems, newItems, options = {}) {
    const {
      reopenTicketDisplayFromBumped = false,
      reopenAt = null,
      suppressAddonDisplayItemIds = new Set(),
      effectiveCourseByItemId = new Map()
    } = options;
    const seen = new Set();
    let merged = [];
    for (const existing of existingItems) {
      const match = newItems.find(i => normalizeId(i.ID) === normalizeId(existing.ID));
      const mergedItem = match ? {
        ...existing,
        ...match
      } : existing;
      const normalizedId = normalizeId(mergedItem?.ID);
      const forcedCourse = effectiveCourseByItemId.get(normalizedId);
      const finalMergedItem = Number.isFinite(Number(forcedCourse))
        ? {
            ...mergedItem,
            course: Number(forcedCourse)
          }
        : mergedItem;
      merged.push(reopenTicketDisplayFromBumped && reopenAt ? reopenTicketItemForDisplay(finalMergedItem, reopenAt, {
        suppressAddonDisplay: suppressAddonDisplayItemIds.has(normalizedId)
      }) : finalMergedItem);
      seen.add(normalizeId(existing.ID));
    }
    for (const newItem of newItems) {
      const id = normalizeId(newItem.ID);
      if (!seen.has(id)) {
        const forcedCourse = effectiveCourseByItemId.get(id);
        const incomingIsAddon = !!newItem?.isAddon || isAddonFlag(newItem?.is_addon) || isAddonFlag(newItem?.isaddon);
        const mergedItem = {
          ...newItem,
          ...(Number.isFinite(Number(forcedCourse)) ? { course: Number(forcedCourse) } : {}),
          isAddon: incomingIsAddon,
          is_addon: incomingIsAddon ? 1 : 0,
          isaddon: incomingIsAddon ? 1 : 0,
          isBumped: false,
          createdAt: new Date().toISOString()
        };
        merged.push(reopenTicketDisplayFromBumped && reopenAt ? reopenTicketItemForDisplay(mergedItem, reopenAt, {
          suppressAddonDisplay: suppressAddonDisplayItemIds.has(id)
        }) : mergedItem);
      }
    }
    const nonSeparators = merged.filter(i => !(i.isCourseSeparator && String(i.ID || "").startsWith("sep-auto-")));
    const syntheticSeparator = merged.find(i => i.isCourseSeparator && String(i.ID || "").startsWith("sep-auto-"));
    if (syntheticSeparator) {
      merged = [...nonSeparators, syntheticSeparator];
    } else {
      merged = nonSeparators;
    }
    return merged;
  }

  // Inserts a hydrated ticket (or pages) into the orders list based on rush/fired/normal priority.
  function insertTicketByPriority(prev, newCards, isRush, isFired, ticketId, mergeMode) {
    let remaining = prev;
    if (mergeMode === "merge") {
      const hasOld = prev.some(p => p.ticket_instance_id === ticketId);
      if (hasOld) {
        remaining = prev.filter(p => p.ticket_instance_id !== ticketId);
      }
    }
    let insertIndex = 0;
    for (; insertIndex < remaining.length; insertIndex++) {
      const o = remaining[insertIndex];
      const oRush = !!o.isRush;
      const oFired = !!o.isFired;
      if (isRush && !oRush) break;
      if (!isRush && isFired && !(oRush || oFired)) break;
      if (!isRush && !isFired) continue;
    }
    return [...remaining.slice(0, insertIndex), ...newCards, ...remaining.slice(insertIndex)];
  }

  // Converts a raw order object into one or more "hydrated" ticket cards for display.
  function hydrateOrder(rawOrder, categoryMap, settings, existingItems = [], preservedCreatedAt = null, existingMessages = [], alreadyHandledMessages = new Set()) {
    const {
      max_items_per_ticket = Infinity,
      coursing,
      use_course_separator,
      group_by_category,
      sync_courses_with_expo
    } = settings;
    const normalizedItems = Array.isArray(rawOrder.Item) ? rawOrder.Item : [rawOrder.Item];
    // This is the order received by the KDS before it tags, courses, sorts, or
    // paginates items. It can be matched to backend `ticket_item_order_stored`.
    const receivedItemOrder = summarizeItemOrder(normalizedItems);
    const orderTags = {
      firedCategories: new Set(),
      displayMessages: [],
      hasRush: rawOrder?.isRush || false,
      parkedAt: normalizeStoredUtcDateTime(rawOrder?.parkedAt ?? rawOrder?.parked_at ?? null),
      unparkTime: normalizeStoredUtcDateTime(rawOrder?.unparkTime ?? rawOrder?.unpark_time ?? null),
      _voidReasons: new Set(),
      _pendingMsgEnvelopes: []
    };
    const taggedItems = normalizedItems.map(item => tagSpecialItem(item, {
      ...orderTags,
      _items: normalizedItems,
      _alreadyHandledMessages: alreadyHandledMessages
    }));
    if (orderTags._pendingMsgEnvelopes.length) {
      for (const env of orderTags._pendingMsgEnvelopes.splice(0)) {
        const suppress = env.normLines.some(L => orderTags._voidReasons.has(L));
        if (!suppress) {
          const unique = env.rawLines.filter(line => !orderTags.displayMessages.includes(line));
          unique.forEach(line => orderTags.displayMessages.push(line));
        }
        if (env.msgKey) orderTags._alreadyHandledMessages?.add(env.msgKey);
        env.item.isMessage = true;
        env.item.isBumped = true;
      }
    }
    let hydratedItems = taggedItems.filter(item => !item.isFireItem);
    if (coursing) {
      const useSyncedExpoCourse = role !== "expo" && !!sync_courses_with_expo;
      logCourseSync("hydrate-coursing-start", {
        order_id: rawOrder.order_id ?? rawOrder.ID ?? null,
        useSyncedExpoCourse,
        itemCount: hydratedItems.length,
        addonCount: hydratedItems.filter(i => !!i?.isAddon || isAddonFlag(i?.is_addon)).length
      });
      if (use_course_separator) {
        logCourseSync("hydrate-before-separator-preserve", {
          order_id: rawOrder.order_id ?? rawOrder.ID ?? null,
          ticket_instance_id: rawOrder.ticket_instance_id ?? null,
          incomingItems: hydratedItems.map(item => ({
            id: normalizeId(item?.ID),
            name: String(item?.Name || "").slice(0, 60),
            isSeparator: !!item?.isCourseSeparator,
            incomingCourse: Number.isFinite(Number(item?.course)) ? Number(item.course) : null,
            status: item?.status ?? null,
            isBumped: !!item?.isBumped,
            isFired: !!item?.isFired
          })),
          existingItems: (existingItems || []).map(item => ({
            id: normalizeId(item?.ID),
            name: String(item?.Name || "").slice(0, 60),
            isSeparator: !!item?.isCourseSeparator,
            existingCourse: Number.isFinite(Number(item?.course)) ? Number(item.course) : null,
            status: item?.status ?? null,
            isBumped: !!item?.isBumped,
            isFired: !!item?.isFired
          }))
        });
        hydratedItems =
          applyPreservedCourseSeparatorCoursing(hydratedItems, existingItems) ||
          applyCourseValueSeparatorCoursing(hydratedItems, "db-course-refresh") ||
          applyCourseSeparatorCoursing(hydratedItems);
        logCourseSync("hydrate-after-separator-preserve", {
          order_id: rawOrder.order_id ?? rawOrder.ID ?? null,
          ticket_instance_id: rawOrder.ticket_instance_id ?? null,
          outputItems: hydratedItems.map(item => ({
            id: normalizeId(item?.ID),
            name: String(item?.Name || "").slice(0, 60),
            isSeparator: !!item?.isCourseSeparator,
            course: Number.isFinite(Number(item?.course)) ? Number(item.course) : null,
            status: item?.status ?? null,
            isBumped: !!item?.isBumped,
            isFired: !!item?.isFired
          }))
        });
      } else {
        hydratedItems = applyCategoryBasedCoursing(hydratedItems, categoryMap);
      }

      // Prep fallback: new add-ons should land on the ticket's last course in realtime.
      // If Expo/DB has authoritative course, that will be applied below.
      const orderIdForSync = rawOrder.order_id ?? rawOrder.ID ?? null;
      logAddonDupCourse("hydrate-before-addon-fallback", {
        ticket_instance_id: rawOrder.ticket_instance_id ?? null,
        order_id: orderIdForSync,
        role,
        use_course_separator: !!use_course_separator,
        sync_courses_with_expo: !!sync_courses_with_expo,
        items: hydratedItems.map(getAddonCourseDebugSnapshot)
      });
      hydratedItems = applyPrepAddonFallbackCoursing(hydratedItems, orderIdForSync);

      // Sync option: Expo remains source-of-truth, but we keep local method as fallback.
      if (useSyncedExpoCourse) {
        hydratedItems = applyExpoSyncedCourses(hydratedItems, orderIdForSync);
      }
      hydratedItems = applyCourseCookStartDelays(hydratedItems);
      logAddonDupCourse("hydrate-final-courses-and-timers", {
        ticket_instance_id: rawOrder.ticket_instance_id ?? null,
        order_id: orderIdForSync,
        items: hydratedItems.map(getAddonCourseDebugSnapshot)
      });
    }
    if (coursing && !use_course_separator) {
      hydratedItems = sortByCourseAndCategoryRanking(hydratedItems, categoryMap);
    }
    if (!coursing) {
      hydratedItems = applyCourseCookStartDelays(hydratedItems);
    }
    hydratedItems = applyPreservedFiredState(hydratedItems, existingItems);
    hydratedItems = hydratedItems.map(item => tagFiredItems(item, orderTags));
    hydratedItems = hydratedItems.map(item => {
      const seat = deriveSeat(item);
      if (seat != null) {
        item.seat_number = String(seat);
        item.PreModifier = item.PreModifier || {};
        if (!item.PreModifier.S0 || !/\d+/.test(String(item.PreModifier.S0))) {
          item.PreModifier.S0 = `Seat ${seat}`;
        }
      }
      return item;
    });
    const displayedItemOrder = summarizeItemOrder(hydratedItems);
    const displayOrderChanged = !sameItemSequence(receivedItemOrder, displayedItemOrder);
    logKdsActivity("kds.ticket_item_order", {
      ticket_instance_id: rawOrder.ticket_instance_id ?? null,
      order_id: rawOrder.order_id ?? rawOrder.ID ?? null,
      source_item_count: normalizedItems.length,
      displayed_item_count: hydratedItems.length,
      coursing: !!coursing,
      use_course_separator: !!use_course_separator,
      category_ranking_sort_applied: !!coursing && !use_course_separator,
      display_order_changed: displayOrderChanged,
      displayed_order_is_alphabetical: isAlphabeticalItemOrder(
        displayedItemOrder.filter(item => !item.is_separator)
      ),
      received_order: receivedItemOrder,
      displayed_order: displayedItemOrder,
    });
    const cards = paginateItems(hydratedItems, max_items_per_ticket, rawOrder.ticket_instance_id, preservedCreatedAt || rawOrder.createdAt || new Date());
    const orderMeta = {
      order_id: rawOrder.order_id ?? rawOrder.ID ?? null,
      pos_terminal: rawOrder.pos_terminal ?? rawOrder.PosTerminal ?? null,
      server_name: rawOrder.server_name ?? rawOrder.ServerName ?? null,
      table_name: rawOrder.table_name ?? rawOrder.GuestTable ?? null,
      destination: rawOrder.destination ?? rawOrder.Destination ?? null,
      order_type: rawOrder.order_type ?? rawOrder.OrderType ?? null,
      user_info: rawOrder.user_info ?? rawOrder.UserInfo ?? null,
      status: rawOrder.status ?? 'pending',
      parkedAt: normalizeStoredUtcDateTime(rawOrder.parkedAt ?? rawOrder.parked_at ?? null),
      unparkTime: normalizeStoredUtcDateTime(rawOrder.unparkTime ?? rawOrder.unpark_time ?? null),
      ticket_bump_display_boxes: Array.isArray(rawOrder.ticket_bump_display_boxes) ? rawOrder.ticket_bump_display_boxes : [],
      course_bump_display_boxes: recomputeCourseDisplayBoxes({
        items: hydratedItems,
        course_bump_display_boxes: rawOrder?.course_bump_display_boxes && typeof rawOrder.course_bump_display_boxes === "object" ? rawOrder.course_bump_display_boxes : {}
      }),
      ...Object.fromEntries(Object.entries(rawOrder).filter(([k, v]) => ["covers", "allergies", "notes"].includes(k)))
    };
    const newMessages = orderTags.displayMessages.filter(msg => !(existingMessages.includes(msg) && !orderTags._items?.some(i => i.Name === "* MESSAGE *")));
    const allMessages = [...existingMessages, ...newMessages];
    const orderMessages = [...new Set(allMessages.map(s => String(s).trim()))];
    cards.forEach(page => {
      page.isRush = !!rawOrder.isRush;
      page.isFired = !!rawOrder.isFired;
      page.order_messages = orderMessages;
      Object.assign(page, orderMeta);
      page.isParked = page.status === 'parked';
    });
    return cards;
  }

  // Normalize for comparison (collapse spaces, uppercase)
  const normKey = s => String(s || "").replace(/\s+/g, " ").trim().toUpperCase();
  function getVoidReasonFromPreModifier(pm) {
    if (!pm || typeof pm !== "object") return null;
    const val = String(pm.S1 ?? "").trim();
    if (!val || /^SEAT\s*#?/i.test(val)) return null;
    return val;
  }
  function normalizePreparationMinutes(item) {
    const raw =
      item?.preparationTime ??
      item?.PreparationTime ??
      item?.preparation_time ??
      item?.prepTime ??
      null;
    if (raw == null || raw === "") return null;
    const minutes = Number(raw);
    return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
  }
  function getItemCookSeconds(item) {
    const cookMinutes = normalizePreparationMinutes(item);
    return cookMinutes != null ? Math.round(cookMinutes * 60) : null;
  }
  function applyCourseCookStartDelays(items = []) {
    const maxCookByCourse = new Map();
    for (const item of items || []) {
      if (item?.isCourseSeparator || item?.isMessage || item?.isFireItem) continue;
      const cookSeconds = getItemCookSeconds(item);
      if (cookSeconds == null) continue;
      const course = Number.isFinite(Number(item?.course)) ? Number(item.course) : 0;
      maxCookByCourse.set(course, Math.max(maxCookByCourse.get(course) || 0, cookSeconds));
    }

    return (items || []).map((item) => {
      if (item?.isCourseSeparator || item?.isMessage || item?.isFireItem) return item;
      const cookSeconds = getItemCookSeconds(item);
      const course = Number.isFinite(Number(item?.course)) ? Number(item.course) : 0;
      const maxCookSeconds = maxCookByCourse.get(course) || 0;
      const startDelaySeconds = cookSeconds == null ? 0 : Math.max(0, maxCookSeconds - cookSeconds);
      return {
        ...item,
        cookTimeSeconds: cookSeconds,
        cookStartDelaySeconds: startDelaySeconds,
      };
    });
  }
  function tagSpecialItem(item, orderTags) {
    const preparationMinutes = normalizePreparationMinutes(item);
    if (preparationMinutes != null) {
      item.preparationTime = preparationMinutes;
    }
    item.isAddon = !!item.isAddon || item.is_addon === 1 || item.is_addon === "1" || item.is_addon === true || item.isaddon === 1 || item.isaddon === true;
    item.showsAddon = !!item.isAddon && !item.suppressAddonDisplay;

    // --- Voids ---
    // --- Voids ---
    if (item.status === "voided" || item.TransType === 2) {
      item.isVoided = true;
      item.isBumped = true;

      // Add void reason token (e.g. "KITCHEN ERROR")
      const reason = getVoidReasonFromPreModifier(item.PreModifier);
      if (reason) {
        item.voidReason = item.voidReason || reason;
        orderTags._voidReasons?.add(normKey(reason));
      }

      // Add the voided item's name token too (e.g. "SOUP DU JOUR CUP")
      const nm = String(item?.Name || "").trim();
      if (nm) {
        orderTags._voidReasons?.add(normKey(nm));
      }
    }
    const statusLower = String(item.status || "").toLowerCase();
    if (statusLower === "parked") {
      item.isParked = true;
      item.parkedAt = item.parkedAt ?? item.parked_at ?? orderTags?.parkedAt ?? null;
    }
    if (statusLower === "bumped") {
      item.isBumped = true;
      // prefer your own field first, but accept DB naming too
      item.bumpedAt = item.bumpedAt ?? item.bumped_at ?? item.BumpedAt ?? null;
    }
    const name = (item?.Name || "").trim();
    item.Category = decodeHtml(item?.Category || "");
    const decodedCategory = item.Category;
    const condiments = Array.isArray(item.Condiment) ? item.Condiment : item.Condiment ? [item.Condiment] : [];

    // --- * MESSAGE * envelope ---
    if (name === "* MESSAGE *") {
      const rawKey = String(item.ID || "").trim();
      const msgKey = normalizeId(rawKey);
      if (msgKey && orderTags._alreadyHandledMessages?.has(msgKey)) {
        item.isMessage = true;
        item.isBumped = true;
        return item;
      }
      const freeformLines = [];
      for (const c of condiments) {
        const val = String(c?.Name || "").trim();
        if (!val) continue;
        const upper = val.toUpperCase();
        if (upper.startsWith("FIRE NEXT COURSE")) {
          const courseMap = new Map();
          for (const i of orderTags._items || []) {
            const eligible = typeof i.course === "number" && !i.isFired && !i.isBumped && !i.isCourseSeparator && !i.isMessage && !i.isFireItem;
            if (eligible) {
              if (!courseMap.has(i.course)) courseMap.set(i.course, []);
              courseMap.get(i.course).push(i);
            }
          }
          if (courseMap.size) {
            const sorted = [...courseMap.entries()].sort(([a], [b]) => a - b);
            for (const [courseNumber, itemsToFire] of sorted) {
              const stillUnfired = itemsToFire.filter(x => !x.isFired);
              if (stillUnfired.length) {
                const nowISO = new Date().toISOString();
                stillUnfired.forEach(x => {
                  x.isFired = true;
                  x.firedAt = x.firedAt || nowISO;
                });
                item._hasFiredCourse = true;
                orderTags.displayMessages.push(`FIRE COURSE ${courseNumber + 1}`);
                break;
              }
            }
          }
          continue;
        }
        const fireSpecificMatch = upper.match(/^FIRE COURSE\s+(\d+)\b/);
        if (fireSpecificMatch) {
          const requestedCourse = Number(fireSpecificMatch[1]) - 1;
          if (Number.isFinite(requestedCourse) && requestedCourse >= 0) {
            const targetItems = (orderTags._items || []).filter(i => typeof i.course === "number" && i.course === requestedCourse && !i.isFired && !i.isBumped && !i.isCourseSeparator && !i.isMessage && !i.isFireItem);
            if (targetItems.length) {
              const nowISO = new Date().toISOString();
              targetItems.forEach(x => {
                x.isFired = true;
                x.firedAt = x.firedAt || nowISO;
              });
              item._hasFiredCourse = true;
              orderTags.displayMessages.push(`FIRE COURSE ${requestedCourse + 1}`);
            }
          }
          continue;
        }
        freeformLines.push(val);
      }
      const normLines = freeformLines.map(normKey);
      if ((orderTags._voidReasons?.size || 0) > 0) {
        const isVoidEcho = normLines.some(L => orderTags._voidReasons.has(L));
        if (!isVoidEcho && freeformLines.length) {
          const uniqueNew = freeformLines.filter(line => !orderTags.displayMessages.includes(line));
          uniqueNew.forEach(line => orderTags.displayMessages.push(line));
        }
        if (msgKey) orderTags._alreadyHandledMessages?.add(msgKey);
        item.isMessage = true;
        item.isBumped = true;
        return item;
      }
      orderTags._pendingMsgEnvelopes?.push({
        item,
        msgKey,
        rawLines: freeformLines,
        normLines
      });
      item.isMessage = true;
      item.isBumped = true;
      return item;
    }

    // --- Course Separator ---
    if (/^\*{3,}.*\*{3,}$/.test(name) || decodedCategory === "Course Separator") {
      item.isCourseSeparator = true;
      return item;
    }

    // --- FIRE line (non-message) ---
    if (name === "------ FIRE ------" || name.toUpperCase().includes("FIRE") && name !== "* MESSAGE *" || typeof item.ID === "string" && item.ID.includes("FIRE")) {
      item.isFireItem = true;
      for (const c of condiments) {
        const match = (c?.Name || "").match(/^--(.+?)--$/);
        if (match) {
          const category = match[1].trim();
          orderTags.firedCategories.add(category);
          orderTags.displayMessages.push(`FIRE ${category}`);
        }
      }
      item.isBumped = true;
      return item;
    }
    return item;
  }
  function tagFiredItems(item, orderTags) {
    if (orderTags.firedCategories.has(item.Category)) {
      item.isFired = true;
      item.firedAt = item.firedAt || new Date().toISOString();
    }
    return item;
  }
  function applyCourseSeparatorCoursing(items) {
    let courseIndex = 0;
    const result = [];
    for (const item of items) {
      const isSynthetic = String(item.ID || "").startsWith("sep-auto-");
      if (item.isCourseSeparator && !isSynthetic) {
        item.course = courseIndex;
        courseIndex++;
      } else {
        item.course = courseIndex;
      }
      result.push(item);
    }
    const hasTrailingSeparator = result.length && result[result.length - 1].isCourseSeparator;
    if (!hasTrailingSeparator) {
      result.push({
        ID: `sep-auto-${Date.now()}`,
        Name: "***",
        Category: "Course Separator",
        isCourseSeparator: true,
        isBumped: false,
        course: courseIndex
      });
    }
    return result;
  }
  function applyCourseValueSeparatorCoursing(items, source = "course-values") {
    const normalized = (items || []).map((item, index) => ({
      ...item,
      _incomingIndex: index
    }));
    const utilityItems = normalized.filter(item => item?.isMessage || item?.isFireItem);
    const realItems = normalized.filter(item => !item?.isCourseSeparator && !item?.isMessage && !item?.isFireItem);
    if (!realItems.length) return null;
    const courseValues = Array.from(
      new Set(
        realItems
          .map(item => Number.isFinite(Number(item?._dbCourse)) ? Number(item._dbCourse) : Number(item?.course))
          .filter(Number.isFinite)
      )
    ).sort((a, b) => a - b);
    if (courseValues.length <= 1) {
      logCourseSync("course-value-separator-skip-single-course", {
        source,
        courseValues,
        realItems: realItems.map(item => ({
          id: normalizeId(item?.ID),
          name: String(item?.Name || "").slice(0, 60),
          course: Number.isFinite(Number(item?.course)) ? Number(item.course) : null,
          dbCourse: Number.isFinite(Number(item?._dbCourse)) ? Number(item._dbCourse) : null
        }))
      });
      return null;
    }

    const realItemsWithCourses = realItems.map(item => ({
      ...item,
      course: Number.isFinite(Number(item?._dbCourse)) ? Number(item._dbCourse) : Number(item.course)
    }));
    realItemsWithCourses.sort((a, b) => {
      const aCourse = Number.isFinite(Number(a?.course)) ? Number(a.course) : 0;
      const bCourse = Number.isFinite(Number(b?.course)) ? Number(b.course) : 0;
      if (aCourse !== bCourse) return aCourse - bCourse;
      return Number(a?._incomingIndex ?? 0) - Number(b?._incomingIndex ?? 0);
    });

    const utilityByIndex = new Map();
    for (const item of utilityItems) {
      const idx = Number(item?._incomingIndex ?? 0);
      if (!utilityByIndex.has(idx)) utilityByIndex.set(idx, []);
      utilityByIndex.get(idx).push(item);
    }

    const result = [];
    const pushUtilityAtIndex = incomingIndex => {
      const entries = utilityByIndex.get(incomingIndex) || [];
      for (const entry of entries) {
        const {
          _incomingIndex,
          ...cleanEntry
        } = entry;
        result.push(cleanEntry);
      }
      utilityByIndex.delete(incomingIndex);
    };

    let currentCourse = null;
    realItemsWithCourses.forEach((item, itemIndex) => {
      const itemCourse = Number.isFinite(Number(item?.course)) ? Number(item.course) : 0;
      if (currentCourse != null && itemCourse !== currentCourse) {
        result.push({
          ID: `sep-auto-course-value-${Date.now()}-${itemIndex}`,
          Name: "***",
          Category: "Course Separator",
          isCourseSeparator: true,
          isBumped: false,
          course: currentCourse
        });
      }
      pushUtilityAtIndex(Number(item?._incomingIndex ?? 0));
      const {
        _incomingIndex,
        ...cleanItem
      } = item;
      result.push(cleanItem);
      currentCourse = itemCourse;
    });

    if (currentCourse != null) {
      result.push({
        ID: `sep-auto-course-value-${Date.now()}-tail`,
        Name: "***",
        Category: "Course Separator",
        isCourseSeparator: true,
        isBumped: false,
        course: currentCourse
      });
    }

    for (const entries of utilityByIndex.values()) {
      for (const entry of entries) {
        const {
          _incomingIndex,
          ...cleanEntry
        } = entry;
        result.push(cleanEntry);
      }
    }

    logCourseSync("course-value-separator-coursing", {
      source,
      courseValues,
      itemCount: realItemsWithCourses.length,
      items: realItemsWithCourses.map(item => ({
        id: normalizeId(item?.ID),
        name: String(item?.Name || "").slice(0, 60),
        course: Number.isFinite(Number(item?.course)) ? Number(item.course) : null
      }))
    });
    return result;
  }
  function applyPreservedCourseSeparatorCoursing(items, existingItems = []) {
    const debugContext = {
      incomingCount: Array.isArray(items) ? items.length : 0,
      existingCount: Array.isArray(existingItems) ? existingItems.length : 0,
      incomingSeparatorCount: (items || []).filter(item => item?.isCourseSeparator).length,
      existingSeparatorCount: (existingItems || []).filter(item => item?.isCourseSeparator).length
    };
    const existingRealItems = (existingItems || []).filter(item => !item?.isCourseSeparator && !item?.isMessage && !item?.isFireItem);
    if (!existingRealItems.length) {
      logCourseSync("preserve-separator-skip-no-existing-real-items", debugContext);
      return null;
    }
    const preservedCourseById = new Map();
    for (const item of existingRealItems) {
      const itemId = normalizeId(item?.ID);
      const course = Number(item?.course);
      if (!itemId || !Number.isFinite(course)) continue;
      preservedCourseById.set(itemId, course);
    }
    if (!preservedCourseById.size) {
      logCourseSync("preserve-separator-skip-no-course-map", {
        ...debugContext,
        existingRealItems: existingRealItems.map(item => ({
          id: normalizeId(item?.ID),
          name: String(item?.Name || "").slice(0, 60),
          course: item?.course ?? null,
          status: item?.status ?? null
        }))
      });
      return null;
    }
    const incomingCourseById = new Map();
    for (const item of items || []) {
      if (item?.isCourseSeparator || item?.isMessage || item?.isFireItem) continue;
      const itemId = normalizeId(item?.ID);
      const course = Number(item?.course);
      if (!itemId || !Number.isFinite(course)) continue;
      incomingCourseById.set(itemId, course);
    }
    const separatorCoursed = applyCourseSeparatorCoursing((items || []).map(item => ({ ...item })));
    const normalized = separatorCoursed.map((item, index) => {
      if (item?.isCourseSeparator) {
        return {
          ...item,
          _incomingIndex: index
        };
      }
      const preservedCourse = preservedCourseById.get(normalizeId(item?.ID));
      const hasPreservedCourse = Number.isFinite(preservedCourse);
      const incomingCourse = incomingCourseById.get(normalizeId(item?.ID));
      const hasIncomingCourse = Number.isFinite(incomingCourse);
      const shouldPreferIncomingAddonCourse =
        hasPreservedCourse &&
        hasIncomingCourse &&
        Number(incomingCourse) !== Number(preservedCourse) &&
        (!!item?.isAddon || isAddonFlag(item?.is_addon) || isAddonFlag(item?.isAddon) || isAddonFlag(item?.isaddon));
      if (shouldPreferIncomingAddonCourse) {
        logCourseSync("preserve-separator-addon-incoming-course-overrides-stale-local", {
          id: normalizeId(item?.ID),
          name: String(item?.Name || "").slice(0, 60),
          preservedCourse,
          incomingCourse
        });
      }
      if (!hasPreservedCourse && !item?.isMessage && !item?.isFireItem) {
        logCourseSync("preserve-separator-new-or-unmatched-item", {
          id: normalizeId(item?.ID),
          name: String(item?.Name || "").slice(0, 60),
          separatorDerivedCourse: Number.isFinite(Number(item?.course)) ? Number(item.course) : null,
          incomingCourse: hasIncomingCourse ? incomingCourse : null,
          preservedCourseIds: Array.from(preservedCourseById.keys()).slice(0, 50)
        });
      }
      return {
        ...item,
        _incomingIndex: index,
        course: hasPreservedCourse && !shouldPreferIncomingAddonCourse
          ? preservedCourse
          : hasIncomingCourse
            ? incomingCourse
          : Number.isFinite(Number(item?.course))
            ? Number(item.course)
            : 0
      };
    });
    const courseValues = Array.from(
      new Set(
        normalized
          .filter(item => !item?.isCourseSeparator && !item?.isMessage && !item?.isFireItem)
          .map(item => Number(item?.course))
          .filter(Number.isFinite)
      )
    ).sort((a, b) => a - b);
    if (courseValues.length <= 1) {
      logCourseSync("preserve-separator-skip-single-course", {
        ...debugContext,
        preservedCourseMap: Array.from(preservedCourseById.entries()),
        normalizedCourses: normalized
          .filter(item => !item?.isCourseSeparator && !item?.isMessage && !item?.isFireItem)
          .map(item => ({
            id: normalizeId(item?.ID),
            name: String(item?.Name || "").slice(0, 60),
            course: Number.isFinite(Number(item?.course)) ? Number(item.course) : null
          }))
      });
      return null;
    }
    const utilityItems = normalized.filter(item => item?.isMessage || item?.isFireItem);
    const realItems = normalized.filter(item => !item?.isCourseSeparator && !item?.isMessage && !item?.isFireItem);
    if (!realItems.length) return null;
    realItems.sort((a, b) => {
      const aCourse = Number.isFinite(Number(a?.course)) ? Number(a.course) : 0;
      const bCourse = Number.isFinite(Number(b?.course)) ? Number(b.course) : 0;
      if (aCourse !== bCourse) return aCourse - bCourse;
      return Number(a?._incomingIndex ?? 0) - Number(b?._incomingIndex ?? 0);
    });
    const utilityByIndex = new Map();
    for (const item of utilityItems) {
      const idx = Number(item?._incomingIndex ?? 0);
      if (!utilityByIndex.has(idx)) utilityByIndex.set(idx, []);
      utilityByIndex.get(idx).push(item);
    }
    const result = [];
    const pushUtilityAtIndex = incomingIndex => {
      const entries = utilityByIndex.get(incomingIndex) || [];
      for (const entry of entries) {
        const {
          _incomingIndex,
          ...cleanEntry
        } = entry;
        result.push(cleanEntry);
      }
      utilityByIndex.delete(incomingIndex);
    };
    let currentCourse = null;
    realItems.forEach((item, itemIndex) => {
      const itemCourse = Number.isFinite(Number(item?.course)) ? Number(item.course) : 0;
      if (currentCourse != null && itemCourse !== currentCourse) {
        result.push({
          ID: `sep-auto-preserved-${Date.now()}-${itemIndex}`,
          Name: "***",
          Category: "Course Separator",
          isCourseSeparator: true,
          isBumped: false,
          course: currentCourse
        });
      }
      pushUtilityAtIndex(Number(item?._incomingIndex ?? 0));
      const {
        _incomingIndex,
        ...cleanItem
      } = item;
      result.push(cleanItem);
      currentCourse = itemCourse;
    });
    if (currentCourse != null) {
      result.push({
        ID: `sep-auto-preserved-${Date.now()}-tail`,
        Name: "***",
        Category: "Course Separator",
        isCourseSeparator: true,
        isBumped: false,
        course: currentCourse
      });
    }
    for (const entries of utilityByIndex.values()) {
      for (const entry of entries) {
        const {
          _incomingIndex,
          ...cleanEntry
        } = entry;
        result.push(cleanEntry);
      }
    }
    logCourseSync("preserve-separator-coursing-from-existing", {
      order_id: existingItems?.[0]?.order_id ?? null,
      preservedCourses: courseValues,
      itemCount: realItems.length
    });
    return result;
  }
  function applyCategoryBasedCoursing(items, categoryMap) {
    for (const item of items) {
      if (item.isCourseSeparator) continue;
      const categoryMeta = categoryMap.get(normalizeCategoryKey(item.Category));
      const group = categoryMeta?.category_group;
      item.course = Number.isFinite(Number(group)) && Number(group) > 0 ? Number(group) - 1 : 0;
    }
    return items;
  }
  function applyPreservedFiredState(items, existingItems = []) {
    const firedStateById = new Map();
    for (const item of existingItems || []) {
      const itemId = normalizeId(item?.ID);
      if (!itemId) continue;
      if (!item?.isFired && !item?.firedAt) continue;
      firedStateById.set(itemId, {
        isFired: !!item?.isFired,
        firedAt: item?.firedAt ?? null
      });
    }
    if (!firedStateById.size) return items;
    return (items || []).map(item => {
      if (item?.isCourseSeparator || item?.isMessage || item?.isFireItem) return item;
      if (item?.isFired || item?.firedAt) return item;
      const preserved = firedStateById.get(normalizeId(item?.ID));
      if (!preserved?.isFired && !preserved?.firedAt) return item;
      return {
        ...item,
        isFired: true,
        firedAt: preserved.firedAt ?? item?.firedAt ?? new Date().toISOString()
      };
    });
  }
  function applyExpoSyncedCourses(items, orderId) {
    if (orderId == null) return items;
    const orderKey = String(orderId);
    return items.map(item => {
      if (item.isCourseSeparator || item.isMessage || item.isFireItem) return item;
      const synced = getExpoSyncedCourse(orderKey, item);
      if (synced && Number.isFinite(Number(synced.course))) {
        const before = item.course;
        item.course = Number(synced.course);
        if (before !== item.course && (item?.isAddon || isAddonFlag(item?.is_addon))) {
          logCourseSync("apply-expo-sync-course", {
            order_id: orderKey,
            item_id: item?.ID,
            before,
            after: item.course,
            source: "expo-map"
          });
        }
      } else if (Number.isFinite(Number(item?._dbCourse))) {
        const before = item.course;
        item.course = Number(item._dbCourse);
        if (before !== item.course && (item?.isAddon || isAddonFlag(item?.is_addon))) {
          logCourseSync("apply-expo-sync-course", {
            order_id: orderKey,
            item_id: item?.ID,
            before,
            after: item.course,
            source: "db-course"
          });
        }
      }
      return item;
    });
  }
  function getExpoSyncedCourse(orderKey, item) {
    const fullKey = `${orderKey}::${normalizeId(item?.ID)}`;
    const exact = expoCourseByOrderItemRef.current.get(fullKey);
    if (exact) return exact;
    const isAddon = isAddonFlag(item?.is_addon) || isAddonFlag(item?.isAddon) || isAddonFlag(item?.isaddon);
    if (!isAddon) return null;
    const addonFallbackKey = `${orderKey}::${normalizeItem(item?.ID)}`;
    const addonFallback = expoCourseByOrderItemRef.current.get(addonFallbackKey);
    if (addonFallback?.isAddonFallback) return addonFallback;
    return null;
  }
  function applyPrepAddonFallbackCoursing(items, orderId) {
    if (orderId == null || role === "expo") return items;
    const orderKey = String(orderId);
    const realItems = (items || []).filter(it => !it?.isCourseSeparator && !it?.isMessage && !it?.isFireItem);
    if (!realItems.length) return items;
    const nonAddonCourses = realItems.filter(it => !(it?.isAddon || it?.is_addon === 1 || it?.is_addon === "1" || it?.is_addon === true)).map(it => Number(it?.course)).filter(n => Number.isFinite(n));
    const allCourses = realItems.map(it => Number(it?.course)).filter(n => Number.isFinite(n));
    const coursePool = nonAddonCourses.length ? nonAddonCourses : allCourses;
    if (!coursePool.length) return items;
    const lastCourse = Math.max(...coursePool);
    return items.map(item => {
      if (item?.isCourseSeparator || item?.isMessage || item?.isFireItem) return item;
      const isAddon = !!item?.isAddon || item?.is_addon === 1 || item?.is_addon === "1" || item?.is_addon === true || item?.isaddon === 1 || item?.isaddon === true;
      if (!isAddon) return item;
      const synced = getExpoSyncedCourse(orderKey, item);
      const hasAuthoritative = synced && Number.isFinite(Number(synced.course)) || Number.isFinite(Number(item?._dbCourse));
      if (hasAuthoritative) {
        logAddonDupCourse("prep-addon-fallback-skip-authoritative", {
          order_id: orderKey,
          item: getAddonCourseDebugSnapshot(item),
          syncedCourse: synced?.course ?? null,
          dbCourse: item?._dbCourse ?? null,
          lastCourse
        });
        return item;
      }
      logCourseSync("prep-addon-fallback-last-course", {
        order_id: orderKey,
        item_id: item?.ID,
        assignedCourse: lastCourse
      });
      logAddonDupCourse("prep-addon-fallback-assign-last-course", {
        order_id: orderKey,
        before: getAddonCourseDebugSnapshot(item),
        assignedCourse: lastCourse,
        coursePool
      });
      return {
        ...item,
        course: lastCourse
      };
    });
  }
  function sortByCourseAndCategoryRanking(items, categoryMap) {
    const result = [...items];
    return result.sort((a, b) => {
      if (a.course !== b.course) return a.course - b.course;
      if (a.isCourseSeparator) return 1;
      if (b.isCourseSeparator) return -1;
      const aIsAddon = !!a?.isAddon || isAddonFlag(a?.is_addon) || isAddonFlag(a?.isaddon);
      const bIsAddon = !!b?.isAddon || isAddonFlag(b?.is_addon) || isAddonFlag(b?.isaddon);

      // Keep add-ons at the end of their course while preserving add-on arrival order.
      if (aIsAddon !== bIsAddon) return aIsAddon ? 1 : -1;
      if (aIsAddon && bIsAddon) {
        const aCreated = new Date(a?.createdAt).getTime();
        const bCreated = new Date(b?.createdAt).getTime();
        const safeACreated = Number.isFinite(aCreated) ? aCreated : Number.POSITIVE_INFINITY;
        const safeBCreated = Number.isFinite(bCreated) ? bCreated : Number.POSITIVE_INFINITY;
        if (safeACreated !== safeBCreated) return safeACreated - safeBCreated;
      }
      const aMeta = categoryMap.get(normalizeCategoryKey(a.Category));
      const bMeta = categoryMap.get(normalizeCategoryKey(b.Category));
      const aRank = Number(aMeta?.ranking);
      const bRank = Number(bMeta?.ranking);
      const safeARank = Number.isFinite(aRank) ? aRank : Number.POSITIVE_INFINITY;
      const safeBRank = Number.isFinite(bRank) ? bRank : Number.POSITIVE_INFINITY;
      if (safeARank !== safeBRank) return safeARank - safeBRank;
      const aCategory = normalizeCategoryKey(a.Category);
      const bCategory = normalizeCategoryKey(b.Category);
      if (aCategory !== bCategory) return aCategory.localeCompare(bCategory);
      const aName = String(a?.Name || "");
      const bName = String(b?.Name || "");
      if (aName !== bName) return aName.localeCompare(bName);
      return 0;
    });
  }
  function paginateItems(items, maxItems, ticketId, createdAt, rawOrder = {}) {
    const cards = [];
    let cardItems = [];
    let cardIndex = 0;
    const sharedOrderMeta = {
      ticket_instance_id: ticketId,
      createdAt,
      GuestTable: rawOrder.GuestTable,
      ServerName: rawOrder.ServerName,
      Destination: rawOrder.Destination,
      UserInfo: rawOrder.UserInfo,
      ID: rawOrder.ID,
      PosTerminal: rawOrder.PosTerminal,
      OrderType: rawOrder.OrderType,
      isRush: rawOrder.isRush,
      isFired: rawOrder.isFired
    };
    for (const item of items) {
      if (cardItems.length >= maxItems) {
        cards.push({
          ...sharedOrderMeta,
          card: ++cardIndex,
          items: cardItems,
          order_messages: []
        });
        cardItems = [];
      }
      cardItems.push(item);
    }
    if (cardItems.length) {
      cards.push({
        ...sharedOrderMeta,
        card: ++cardIndex,
        items: cardItems,
        order_messages: []
      });
    }
    return cards;
  }
  function moveTicketGlobal(fromIndex, toIndex) {
    setOrders(prev => {
      const arr = [...prev];
      if (fromIndex < 0 || fromIndex >= arr.length) return prev;
      if (toIndex < 0) toIndex = 0;
      if (toIndex > arr.length) toIndex = arr.length;
      const [moved] = arr.splice(fromIndex, 1);
      if (fromIndex < toIndex) toIndex -= 1;
      arr.splice(toIndex, 0, moved);
      return arr;
    });
  }

  // --- Parking helpers ---

  const pendingAutoUnparksRef = useRef(new Set());
  const pendingFireUnparksRef = useRef(new Set());
  const [lastUnparkSignal, setLastUnparkSignal] = useState(null);
  function parkTicket(ticket_instance_id, options = {}) {
    if (!ticket_instance_id) return;
    const nowIso = new Date().toISOString();
    const unparkTime = options?.unparkTime ?? null;
    const target = ordersRef.current.find(order => order.ticket_instance_id === ticket_instance_id);
    setOrders(prev => applyParkStateToOrders(prev, {
      ticket_instance_id,
      order_id: target?.order_id ?? null,
      parkedAt: nowIso,
      unparkTime,
      isParked: true
    }));
    apiFetch(`/orders/${ticket_instance_id}/park`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        station_id: stationId,
        sender_screen_id: mountIdRef.current,
        sender_role: role,
        sender_station_id: stationId,
        unpark_time: unparkTime
      })
    }).catch(err => {
      console.error('Failed to persist park in DB:', err);
    });
  }
  function unparkTicket(ticket_instance_id, options = {}) {
    if (!ticket_instance_id) return;
    const now = new Date();
    const target = ordersRef.current.find(order => order.ticket_instance_id === ticket_instance_id);
    const ticketId = String(ticket_instance_id);
    setLastUnparkSignal({
      ticket_instance_id,
      order_id: target?.order_id ?? null,
      source: options?.source || (pendingAutoUnparksRef.current.has(ticketId) ? "auto-local" : "manual-local"),
      nonce: Date.now()
    });
    setOrders(prev => {
      const sourceByTicket = new Map(prev.map(order => [String(order?.ticket_instance_id || ""), order]));
      const beforeOrder = prev.find(order => {
        const sameOrder = String(order?.order_id ?? "") === String(target?.order_id ?? "");
        const sameTicket = normalizeTicket(order?.ticket_instance_id) === normalizeTicket(ticket_instance_id);
        return sameOrder || sameTicket;
      });
      const updated = applyParkStateToOrders(prev, {
        ticket_instance_id,
        order_id: target?.order_id ?? null,
        isParked: false
      });
      const afterOrder = updated.find(order => {
        const sameOrder = String(order?.order_id ?? "") === String(target?.order_id ?? "");
        const sameTicket = normalizeTicket(order?.ticket_instance_id) === normalizeTicket(ticket_instance_id);
        return sameOrder || sameTicket;
      });
      return updated.map(order => {
        const sameOrder = String(order?.order_id ?? "") === String(target?.order_id ?? "");
        const sameTicket = normalizeTicket(order?.ticket_instance_id) === normalizeTicket(ticket_instance_id);
        if (!sameOrder && !sameTicket) return order;
        const sourceOrder = sourceByTicket.get(String(order?.ticket_instance_id || "")) || order;
        const orderParkedAt = sourceOrder?.parkedAt ? new Date(sourceOrder.parkedAt) : null;
        const orderPausedDelta = orderParkedAt ? Math.max(0, Math.floor((now - orderParkedAt) / 1000)) : 0;
        return {
          ...order,
          totalPausedSeconds: (order.totalPausedSeconds || 0) + orderPausedDelta,
          items: (order.items || []).map((item, idx) => {
            const sourceItem = sourceOrder?.items?.[idx] || item;
            const itemWasParked = String(sourceItem?.status || "").toLowerCase() === "parked" || sourceItem?.isParked;
            const itemParkedAt = sourceItem?.parkedAt ? new Date(sourceItem.parkedAt) : orderParkedAt || now;
            const itemPausedDelta = itemWasParked ? Math.max(0, Math.floor((now - itemParkedAt) / 1000)) : 0;
            return {
              ...item,
              totalPausedSeconds: (item.totalPausedSeconds || 0) + itemPausedDelta
            };
          })
        };
      });
    });
    apiFetch(`/orders/${ticket_instance_id}/unpark`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        station_id: stationId,
        sender_screen_id: mountIdRef.current,
        sender_role: role,
        sender_station_id: stationId
      })
    }).catch(err => {
      console.error('Failed to persist unpark in DB:', err);
    }).finally(() => {
      pendingAutoUnparksRef.current.delete(ticketId);
      pendingFireUnparksRef.current.delete(ticketId);
    });
  }
  unparkTicketRef.current = unparkTicket;
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const now = Date.now();
      for (const order of ordersRef.current || []) {
        if (!order?.isParked || !order?.unparkTime || !order?.ticket_instance_id) continue;
        const ticketId = String(order.ticket_instance_id);
        const dueAt = Date.parse(order.unparkTime);
        if (!Number.isFinite(dueAt) || dueAt > now) continue;
        if (pendingAutoUnparksRef.current.has(ticketId)) continue;
        pendingAutoUnparksRef.current.add(ticketId);
        unparkTicketRef.current(order.ticket_instance_id);
      }
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [stationId, stationTimezone]);

  // --- Emit helpers (standardized names) ---
  function emitBumpTicket(ticket_instance_id) {
    if (!ticket_instance_id) return;
    // Separate tickets sync through their item bumps. A whole-order socket
    // event would also complete the original/addon sibling tickets.
    if (settingsRef.current?.merge_mode !== "merge") return;
    const meta = {
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    };
    const order = ordersRef.current.find(o => o.ticket_instance_id === ticket_instance_id);
    socket.emit("bump-ticket", {
      ticket_instance_id,
      order_id: order?.order_id ?? null,
      user_info: getUserInfoFor(order),
      ...meta
    });
    track(bumpTicketEmittersRef, ticket_instance_id || order?.order_id, {
      ...meta,
      kind: "bump-ticket"
    });
    bumpedTicketHistoryRef.current.add(normalizeTicket(ticket_instance_id));
    if (order?.order_id != null) {
      bumpedOrderHistoryRef.current.add(String(order.order_id));
    }
  }
  function emitUnbumpTicket({
    ticket_instance_id,
    order_id,
    unbump_items = false
  } = {}) {
    if (!ticket_instance_id && order_id == null) return;
    const meta = {
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    };
    socket.emit("unbump-ticket", {
      ticket_instance_id: ticket_instance_id ?? null,
      order_id: order_id ?? null,
      unbump_items: unbump_items === true,
      ...meta
    });
  }
  function emitBumpCourse(ticket_instance_id, course, activityContext = {}) {
    if (!ticket_instance_id || typeof course !== "number") return;
    const meta = {
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    };
    const order = ordersRef.current.find(o => o.ticket_instance_id === ticket_instance_id);
    const courseItems = (order?.items || []).filter(i => i.course === course && !i.isCourseSeparator && !i.isMessage);
    const course_item_ids = courseItems.map(i => i.ID).filter(Boolean);
    socket.emit("bump-course", {
      ticket_instance_id,
      order_id: order?.order_id ?? null,
      course,
      course_item_ids,
      course_item_names: courseItems.map(i => i.Name).filter(Boolean),
      course_item_count: courseItems.length,
      user_info: getUserInfoFor(order),
      operation_id: activityContext.operation_id ?? null,
      ...meta
    });
    track(bumpCourseEmittersRef, `${ticket_instance_id}::${course}`, {
      ...meta,
      order_id: order?.order_id ?? null,
      course_index: course,
      course_number: Number.isFinite(course) ? course + 1 : null,
      course_item_count: courseItems.length,
      course_item_names: courseItems.map(i => i.Name).filter(Boolean),
      kind: "bump-course"
    });
  }
  function emitBumpItem(ticket_instance_id, item, isBumped, activityContext = {}) {
    if (!ticket_instance_id || !item?.ID) return;
    const meta = {
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    };
    const order = ordersRef.current.find(o => o.ticket_instance_id === ticket_instance_id);
    socket.emit("bump-item", {
      ticket_instance_id,
      order_id: order?.order_id ?? null,
      item_id: item.ID,
      item_name: item.Name,
      course: item.course,
      category: item.Category,
      isBumped: !!isBumped,
      seat_number: (() => {
        const s = deriveSeat(item);
        return s != null ? String(s) : null;
      })(),
      bumpedAt: isBumped ? new Date().toISOString() : null,
      user_info: getUserInfoFor(order),
      operation_id: activityContext.operation_id ?? null,
      ...meta
    });
    track(bumpItemEmittersRef, item.ID, {
      ...meta,
      order_id: order?.order_id ?? null,
      item_name: item.Name,
      category: item.Category,
      course: item.course,
      isBumped: !!isBumped,
      kind: "bump-item"
    });
  }
  function emitFireTicket(payload = {}) {
    let {
      ticket_instance_id,
      order_id
    } = payload;

    // Try to resolve either id if missing
    if (!order_id && ticket_instance_id) {
      const m = ordersRef.current.find(o => String(o.ticket_instance_id).split("-")[0] === String(ticket_instance_id).split("-")[0]);
      order_id = m?.order_id ?? null;
    }
    if (!ticket_instance_id && order_id) {
      const m = ordersRef.current.find(o => String(o.order_id) === String(order_id));
      ticket_instance_id = m?.ticket_instance_id ?? null;
    }
    const o = ordersRef.current.find(x => ticket_instance_id && String(x.ticket_instance_id).split("-")[0] === String(ticket_instance_id).split("-")[0] || order_id != null && String(x.order_id) === String(order_id));
    const totalItems = (o?.items || []).filter(i => !i.isCourseSeparator && !i.isMessage).length;
    const meta = {
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    };
    socket.emit("fire-ticket", {
      ...(ticket_instance_id ? {
        ticket_instance_id
      } : {}),
      ...(order_id != null ? {
        order_id
      } : {}),
      total_items: totalItems,
      user_info: getUserInfoFor(o),
      ...meta
    });

    // optional: track() if you want it to show in your tracking snapshot immediately
    track(fireTicketEmittersRef, ticket_instance_id || order_id, {
      ...(o ? {
        order_id: o.order_id ?? null,
        table_name: o.table_name ?? null,
        destination: o.destination ?? null,
        server_name: o.server_name ?? null
      } : {}),
      kind: "fire-ticket",
      ...meta
    });
  }
  function emitBumpAllCoursesForTicket(ticket_instance_id) {
    if (!ticket_instance_id) return;
    if (settingsRef.current?.merge_mode !== "merge") return;

    // Find the ticket in current state
    const o = ordersRef.current.find(x => String(x.ticket_instance_id).split("-")[0] === String(ticket_instance_id).split("-")[0]);
    if (!o) return;

    // Get all real items and their courses
    const realItems = (o.items || []).filter(i => Number(i.KDSStation) === stationId && !i.isCourseSeparator && !i.isMessage);

    // Unique, numeric courses only
    const courses = Array.from(new Set(realItems.map(i => i.course).filter(c => Number.isFinite(c)))).sort((a, b) => a - b);

    // If no courses are set (coursing off), emit a single synthetic course = 0
    const toEmit = courses.length ? courses : [0];
    toEmit.forEach(course => {
      emitBumpCourse(ticket_instance_id, Number(course));
    });
  }
  function emitVoidItem(ticket_instance_id, item) {
    if (!ticket_instance_id || !item?.ID) return;
    const meta = {
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    };
    const order = ordersRef.current.find(o => o.ticket_instance_id === ticket_instance_id);
    socket.emit("void-item", {
      ticket_instance_id,
      order_id: order?.order_id ?? null,
      item_id: item.ID,
      item_name: item.Name,
      category: item.Category,
      course: item.course,
      voidReason: item.voidReason ?? null,
      seat_number: (() => {
        const s = deriveSeat(item);
        return s != null ? String(s) : null;
      })(),
      user_info: getUserInfoFor(order),
      ...meta
    });
    if (typeof onVoidedItem === "function") {
      onVoidedItem({
        source: "local-emit",
        station_id: stationId,
        order_id: order?.order_id ?? null,
        ticket_instance_id,
        item_id: item.ID
      });
    }
    track(voidItemEmittersRef, item.ID, {
      ...meta,
      order_id: order?.order_id ?? null,
      item_name: item.Name,
      category: item.Category,
      course: item.course,
      voidReason: item.voidReason ?? null,
      kind: "void-item"
    });
  }
  function emitVoidTicket(ticket_instance_id, reason = null) {
    if (!ticket_instance_id) return;
    const meta = {
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    };
    const order = ordersRef.current.find(o => o.ticket_instance_id === ticket_instance_id);
    socket.emit("void-ticket", {
      ticket_instance_id,
      order_id: order?.order_id ?? null,
      voidReason: reason,
      user_info: getUserInfoFor(order),
      ...meta
    });
  }
  function emitParkTicket(ticket_instance_id, parkedAt) {
    if (!ticket_instance_id) return;
    const meta = {
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    };
    const order = ordersRef.current.find(o => o.ticket_instance_id === ticket_instance_id);
    socket.emit("park-ticket", {
      ticket_instance_id,
      order_id: order?.order_id ?? null,
      parkedAt,
      user_info: getUserInfoFor(order),
      ...meta
    });
  }
  function emitUnparkTicket(ticket_instance_id) {
    if (!ticket_instance_id) return;
    const meta = {
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    };
    const order = ordersRef.current.find(o => o.ticket_instance_id === ticket_instance_id);
    socket.emit("unpark-ticket", {
      ticket_instance_id,
      order_id: order?.order_id ?? null,
      user_info: getUserInfoFor(order),
      ...meta
    });
  }
  function emitTicketLocationSync({
    order = null,
    ticketKey = null,
    orderedKeys = [],
    targetGlobalIndex = null
  } = {}) {
    const toStableRef = (candidateOrder, fallbackKey = null) => {
      const orderId = candidateOrder?.order_id ?? null;
      const card = Number.isFinite(Number(candidateOrder?.card)) ? Number(candidateOrder.card) : 0;
      if (orderId != null) {
        return `${String(orderId).trim()}::${card}`;
      }
      if (candidateOrder?.ticket_instance_id) {
        return `${String(candidateOrder.ticket_instance_id).split("-")[0].trim()}::${card}`;
      }
      if (fallbackKey != null) {
        return String(fallbackKey);
      }
      return null;
    };
    const safeOrderedKeys = Array.isArray(orderedKeys) ? orderedKeys.map(value => String(value || "").trim()).filter(Boolean) : [];
    const targetOrder = order || null;
    const orderedRefs = safeOrderedKeys.map(key => {
      const matchingOrder = ordersRef.current.find(candidate => `${candidate.ticket_instance_id}::${candidate.card ?? 0}` === key) || null;
      return toStableRef(matchingOrder, key);
    }).filter(Boolean);
    socket.emit("ticket-location-sync", {
      ticket_key: ticketKey != null ? String(ticketKey) : null,
      ticket_ref: toStableRef(targetOrder, ticketKey),
      ticket_instance_id: targetOrder?.ticket_instance_id ?? null,
      order_id: targetOrder?.order_id ?? null,
      card: Number.isFinite(Number(targetOrder?.card)) ? Number(targetOrder.card) : 0,
      ordered_keys: safeOrderedKeys,
      ordered_refs: orderedRefs,
      target_global_index: Number.isFinite(Number(targetGlobalIndex)) ? Number(targetGlobalIndex) : null,
      user_info: getUserInfoFor(targetOrder),
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    });
  }
  function emitTicketPageSync({
    pageIndex = null
  } = {}) {
    const safePageIndex = Number(pageIndex);
    if (!Number.isFinite(safePageIndex) || safePageIndex < 0) return;
    socket.emit("ticket-page-sync", {
      page_index: safePageIndex,
      sender_screen_id: mountIdRef.current,
      sender_role: role,
      sender_station_id: stationId,
      sentAt: new Date().toISOString()
    });
  }

  // Expose orders and utility refs
  return {
    orders,
    setOrders,
    fetchOrders,
    moveTicketByNewList,
    moveTicketGlobal,
    tapTimers,
    settings,
    stationId,
    role,
    fireCourse,
    fireItem,
    FireAllItems,
    addMessage,
    tracking: trackingSnapshot,
    emitBumpTicket,
    emitBumpCourse,
    emitBumpItem,
    emitFireTicket,
    emitBumpAllCoursesForTicket,
    emitUnbumpTicket,
    emitTicketLocationSync,
    emitTicketPageSync,
    parkTicket,
    unparkTicket,
    lastUnparkSignal,
    screenMountId: mountIdRef.current
  };
}
